import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronDown,
  Code2,
  FileText,
  Gem,
  Layers3,
  Lock,
  LogOut,
  Mail,
  Menu,
  MonitorCheck,
  Network,
  PlugZap,
  Rocket,
  ShieldCheck,
  Sparkles,
  Ticket,
  Timer,
  Users,
  WalletCards,
  X
} from 'lucide-react';
import { api, getCookie, productOptions, submitForm } from './lib/api.js';

const supportEmail = 'support@whitestonecomputing.com';
const supportEmailDisplay = 'Support@WhitestoneComputing.com';
const adminEmail = 'support@whitestonecomputing.com';
const whitestoneLogo = '/wcslogo1.png';
const supportProductOptions = [
  { id: 'none', name: 'None / General' },
  ...productOptions,
  { id: 'other', name: 'Other' }
];

const products = [
  {
    id: 'aibackgroundremover',
    name: 'AI Background Remover',
    category: 'AI utility',
    status: 'Active',
    icon: Sparkles,
    websiteUrl: 'https://cleancutai.com',
    description:
      'An AI-powered utility for quickly removing image backgrounds and preparing clean visuals for business, ecommerce, marketing, and creative use.'
  },
  {
    id: 'hireflow',
    name: 'Hireflow',
    category: 'Hiring workflow',
    status: 'Beta',
    icon: BriefcaseBusiness,
    websiteUrl: 'https://hireflow.app',
    description:
      'A hiring and recruitment workflow platform designed to help organize candidates, job pipelines, applications, and hiring activity.'
  },
  {
    id: 'paystubgen',
    name: 'Paystub Instant',
    category: 'Payment documents',
    status: 'Active',
    icon: WalletCards,
    websiteUrl: 'https://paystubinstant.com',
    description:
      'A lightweight paystub and payroll document generation tool for fast, organized, and professional income documentation workflows.'
  },
  {
    id: 'smartdoctools',
    name: 'SmartDocTools',
    category: 'Document tools',
    status: 'Active',
    icon: FileText,
    websiteUrl: 'https://smartdoctools.com',
    description:
      'A document utility platform for generating, organizing, editing, and managing practical business and personal documents.'
  },
  {
    id: 'avellojewelry',
    name: 'Avello Jewelry',
    category: 'Commerce software',
    status: 'Active',
    icon: Gem,
    websiteUrl: 'https://avellojewelry.com',
    description:
      'A modern jewelry commerce brand and product system under the Whitestone software portfolio, built for polished catalog, content, and storefront workflows.'
  },
  {
    id: 'wakeupalarmsaas',
    name: 'WakeUpAlarmSaaS',
    category: 'Productivity',
    status: 'Beta',
    icon: Timer,
    websiteUrl: 'https://wakecallnow.com',
    description:
      'A productivity and reminder-based alarm platform designed to help users stay on schedule with simple, reliable wake-up and alert tools.'
  }
];

const publicProducts = products;

const apiProducts = [
  {
    slug: 'ai-utility-apis',
    title: 'AI Utility APIs',
    preview: 'Live API access for practical AI image, content, and productivity workflows.',
    overview:
      'AI Utility APIs help teams add focused AI features to customer-facing products without building every workflow from scratch. Use them for media preparation, content support, productivity actions, and repeatable AI-powered tasks that need a clear business purpose.',
    valuePoints: [
      'Add AI-assisted image, content, and productivity features to existing customer workflows.',
      'Support lightweight automation where speed, clarity, and consistency matter.',
      'Give product teams a practical path from idea to usable AI feature.'
    ],
    endpoints: ['POST /v1/ai/images', 'POST /v1/ai/content', 'GET /v1/ai/jobs/{id}']
  },
  {
    slug: 'document-automation-apis',
    title: 'Document Automation APIs',
    preview: 'Customer-ready tools for document generation, conversion, extraction, and business workflow support.',
    overview:
      'Document Automation APIs are built for teams that need documents to move faster through real business workflows. They support generation, conversion, extraction, and structured document tasks for products, internal operations, and customer portals.',
    valuePoints: [
      'Generate and process documents with consistent output and cleaner handoffs.',
      'Support document-heavy business flows such as onboarding, records, and customer requests.',
      'Reduce manual document work while keeping workflows easy to explain.'
    ],
    endpoints: ['POST /v1/document-jobs', 'POST /v1/document-conversions', 'GET /v1/document-jobs/{id}']
  },
  {
    slug: 'saas-automation-apis',
    title: 'SaaS Automation APIs',
    preview: 'Developer resources for repeatable account, support, billing, and product workflows.',
    overview:
      'SaaS Automation APIs help software teams coordinate common product operations across accounts, support, billing, notifications, and customer lifecycle workflows. They are designed for repeatable actions that need reliability and clean integration points.',
    valuePoints: [
      'Connect account, billing, support, and product operations with clearer automation.',
      'Give SaaS teams reusable workflow tools instead of one-off manual processes.',
      'Improve customer operations with more consistent status, access, and support handling.'
    ],
    endpoints: ['POST /v1/accounts/events', 'POST /v1/support/workflows', 'GET /v1/automation-events/{id}']
  },
  {
    slug: 'business-workflow-apis',
    title: 'Business Workflow APIs',
    preview: 'Focused API products for lightweight, practical business utilities customers can rely on.',
    overview:
      'Business Workflow APIs give companies practical building blocks for repeatable tasks, customer-facing utilities, and operational automation. They are shaped around everyday business needs where dependable execution matters more than unnecessary complexity.',
    valuePoints: [
      'Automate repeatable business tasks with simple, customer-ready API products.',
      'Support lightweight utilities for operations, service delivery, and customer workflows.',
      'Create dependable integrations that make common business actions easier to manage.'
    ],
    endpoints: ['POST /v1/workflows/run', 'POST /v1/business-utilities', 'GET /v1/workflows/{id}']
  }
];

const focusAreas = [
  'Clear AI education',
  'Focused software products',
  'Developer-ready API tools',
  'Workflow automation',
  'Document and business utilities',
  'AI news and product updates'
];

const publicStats = [
  ['Product lanes', 5],
  ['Service capabilities', 11],
  ['Quality standards', 6],
  ['Developer lanes', 6]
];

const launchSignals = [
  ['Easy contact', 'Every product gives customers a clear path to ask, request, and get support.'],
  ['Portfolio visibility', 'Whitestone keeps product health, support demand, and customer signals organized as the company grows.'],
  ['Reliable follow-through', 'Support replies, product updates, and service notifications are designed to stay consistent across brands.'],
  ['Room to grow', 'New products can join the Whitestone portfolio without losing the clean experience customers expect.']
];

const operatingModel = [
  {
    title: 'Consistent experience',
    icon: Network,
    text: 'Whitestone products share a commitment to clear onboarding, dependable support, careful product design, and practical daily use.'
  },
  {
    title: 'Focused product lanes',
    icon: MonitorCheck,
    text: 'Each brand solves a specific problem, so customers get useful tools without wading through unnecessary software clutter.'
  },
  {
    title: 'Built to connect',
    icon: PlugZap,
    text: 'AI education, AI news, APIs, software, and support are planned to work together as one professional technology portfolio.'
  }
];

const ecosystemDestinations = [
  { label: 'AI education and news', icon: BookOpen, href: 'https://ai.whitestonecomputing.com', external: true },
  { label: 'Developer APIs', icon: Code2, href: 'https://api.whitestonecomputing.com', external: true },
  { label: 'Operating infrastructure', icon: Network, href: '/about' }
];

function App() {
  return (
    <div className="app-shell">
      <ScrollToTop />
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="*" element={<PublicLayout />} />
      </Routes>
    </div>
  );
}

function PublicLayout() {
  const [adminSession, setAdminSession] = useState(null);
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [newsletterTrigger, setNewsletterTrigger] = useState('first_visit');
  const newsletterAutoShown = useRef(false);
  const location = useLocation();
  const isAdmin = isWhitestoneAdmin(adminSession);

  useEffect(() => {
    api('/api/admin/session')
      .then((session) => setAdminSession(session.admin))
      .catch(() => setAdminSession(null));
  }, []);

  useEffect(() => {
    const openNewsletter = (event) => {
      setNewsletterTrigger(event.detail?.sourceForm || 'manual_cta');
      setNewsletterOpen(true);
    };
    window.addEventListener('whitestone:open-newsletter', openNewsletter);
    return () => window.removeEventListener('whitestone:open-newsletter', openNewsletter);
  }, []);

  useEffect(() => {
    if (!isNewsletterAllowedPath(location.pathname)) return undefined;
    if (newsletterAutoShown.current) return undefined;
    const preference = readNewsletterPreference();
    if (preference === 'subscribed' || preference === 'dismissed') return undefined;
    newsletterAutoShown.current = true;
    const timer = window.setTimeout(() => {
      setNewsletterTrigger('first_visit');
      setNewsletterOpen(true);
    }, 1300);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  function closeNewsletter(reason = 'dismissed') {
    if (reason === 'subscribed') {
      window.localStorage.setItem('wc_newsletter_preference', JSON.stringify({ value: 'subscribed', path: window.location.pathname, savedAt: Date.now() }));
    } else {
      window.localStorage.setItem('wc_newsletter_preference', JSON.stringify({ value: 'dismissed', path: window.location.pathname, savedAt: Date.now() }));
    }
    setNewsletterOpen(false);
  }

  return (
    <>
      <Header isAdmin={isAdmin} />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/ai" element={<AISitePreview />} />
          <Route path="/apis" element={<APISitePreview />} />
          <Route path="/apis/:apiSlug" element={<APIDetailPage />} />
          <Route path="/software" element={<Products />} />
          <Route path="/news" element={<Navigate to="/ai" replace />} />
          <Route path="/products" element={<Products />} />
          <Route path="/work-with-us" element={<WorkWithUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/invite" element={<InviteRedirect />} />
          <Route path="/support" element={<Support />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/ai/privacy" element={<Privacy />} />
          <Route path="/ai/terms" element={<Terms />} />
          <Route path="/apis/privacy" element={<Privacy />} />
          <Route path="/apis/terms" element={<Terms />} />
          <Route path="/news/privacy" element={<Navigate to="/ai/privacy" replace />} />
          <Route path="/news/terms" element={<Navigate to="/ai/terms" replace />} />
          <Route path="/software/privacy" element={<Privacy />} />
          <Route path="/software/terms" element={<Terms />} />
          <Route path="/products/privacy" element={<Privacy />} />
          <Route path="/products/terms" element={<Terms />} />
          <Route path="/copyright" element={<Copyright />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/auth/google/success" element={<OAuthResult success />} />
          <Route path="/auth/google/error" element={<OAuthResult />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer isAdmin={isAdmin} />
      <NewsletterModal
        open={newsletterOpen}
        sourceForm={newsletterTrigger}
        sourcePage={location.pathname}
        onClose={closeNewsletter}
      />
    </>
  );
}

function openNewsletterModal(sourceForm = 'manual_cta') {
  window.dispatchEvent(new CustomEvent('whitestone:open-newsletter', { detail: { sourceForm } }));
}

function readNewsletterPreference() {
  try {
    const preference = JSON.parse(window.localStorage.getItem('wc_newsletter_preference') || 'null');
    if (!preference?.value || !preference?.savedAt) return '';
    const maxAgeMs = 1000 * 60 * 60 * 24 * 45;
    if (Date.now() - preference.savedAt >= maxAgeMs) return '';
    if (preference.value === 'subscribed') return 'subscribed';
    return preference.value === 'dismissed' && preference.path === window.location.pathname ? 'dismissed' : '';
  } catch {
    return '';
  }
}

function isNewsletterAllowedPath(pathname) {
  return !/\/(admin|login|checkout|payment|account|auth|unsubscribe)\b/i.test(pathname);
}

function isValidEmailAddress(value) {
  const email = String(value || '').trim().toLowerCase();
  if (email.length < 6 || email.length > 180 || email.includes('..')) return false;
  return /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(email);
}

function Header({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const links = [
    ['/', 'Home'],
    ['/about', 'About'],
    ['/ai', 'AI Development'],
    ['/apis', 'APIs'],
    ['/software', 'Software'],
    ['/work-with-us', 'Work With Us'],
    ['/contact', 'Contact'],
    ['/support', 'Support']
  ];
  const legalLinks = [
    ['/privacy', 'Privacy Policy'],
    ['/terms', 'Terms of Service'],
    ['/copyright', 'Trademarks / Copyright']
  ];

  useEffect(() => {
    document.body.classList.toggle('nav-open', open);
    if (!open) return () => document.body.classList.remove('nav-open');
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.classList.remove('nav-open');
    };
  }, [open]);

  return (
    <header className="site-header">
      <Link className="brand" to="/" onClick={() => setOpen(false)}>
        <BrandMark />
        <span>
          <strong>Whitestone Computing</strong>
        </span>
      </Link>
      <button
        className="icon-button menu-button"
        type="button"
        aria-controls="primary-navigation"
        aria-expanded={open}
        aria-label={open ? 'Close navigation' : 'Open navigation'}
        onClick={() => setOpen(!open)}
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      {open && <button className="nav-backdrop" type="button" aria-label="Dismiss navigation" onClick={() => setOpen(false)} />}
      <nav id="primary-navigation" className={open ? 'nav open' : 'nav'} aria-label="Primary navigation">
        <div className="nav-drawer-banner" aria-hidden={!open}>
          <BrandMark className="nav-drawer-icon" />
          <div>
            <strong>Whitestone Computing</strong>
            <p>Practical software, AI education, APIs, and digital media for modern teams.</p>
          </div>
        </div>
        <div className="nav-main-links">
          {links.map(([to, label]) => (
            <div className="nav-link-set" key={to}>
              <NavLink to={to} onClick={() => setOpen(false)}>
                {label}
              </NavLink>
            </div>
          ))}
          {isAdmin && (
            <Link className="nav-admin" to="/admin" onClick={() => setOpen(false)}>
              Admin Dashboard
            </Link>
          )}
        </div>
        <div className="nav-legal-links">
          <span>Legal</span>
          {legalLinks.map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setOpen(false)}>
              {label}
            </Link>
          ))}
        </div>
      </nav>
    </header>
  );
}

function BrandMark({ className = 'brand-mark' }) {
  return (
    <span className={className}>
      <img src={whitestoneLogo} alt="" aria-hidden="true" />
    </span>
  );
}

function isWhitestoneAdmin(session) {
  return session?.email?.toLowerCase() === adminEmail;
}

function Home() {
  return (
    <>
      <section className="hero band">
        <div className="hero-grid container">
          <div className="hero-copy">
            <span className="eyebrow">Technology, software, AI, media, and digital platforms</span>
            <h1>Building practical technology brands for the modern web.</h1>
            <p>
              Whitestone Computing builds modern digital products that help businesses work smarter, learn faster, and grow online. Our
              work spans SaaS platforms, AI tools, automation, developer APIs, technology education, media, and B2B software - all
              designed for real users, practical workflows, and long-term value.
            </p>
            <div className="hero-actions">
              <Link className="button primary" to="/about">
                Mission <ArrowRight size={18} />
              </Link>
              <Link className="button secondary" to="/ai">Media</Link>
              <Link className="button secondary" to="/apis">APIs</Link>
              <Link className="button ghost" to="/contact">Contact Us</Link>
            </div>
            <div className="hero-stats" aria-label="Whitestone company metrics">
              {publicStats.map(([label, value]) => (
                <span key={label}>
                  <strong>{value}</strong>
                  {label}
                </span>
              ))}
            </div>
          </div>
          <div className="hero-system" aria-label="Whitestone company operating system">
            <TechConstellation />
            <div className="orbital-core">
              <Layers3 size={34} />
              <span>Connected technology portfolio</span>
            </div>
            {ecosystemDestinations.map(({ label, icon: Icon, href, external }) => (
              <EcosystemLink className="system-node" href={href} external={external} key={label}>
                <Icon size={20} />
                <span>{label}</span>
              </EcosystemLink>
            ))}
          </div>
        </div>
      </section>

      <section className="section container">
        <div className="section-heading section-heading-padded">
          <span className="eyebrow">Mission</span>
          <h2>Practical software that removes friction from everyday workflows.</h2>
          <p>
            Whitestone Computing LLC builds software that feels approachable, useful, and dependable from the first interaction.
            Each product is shaped around a real workflow, clear next steps, and a better everyday experience for customers.
          </p>
          <Link className="underlined-cta" to="/about#mission">
            Learn more <ArrowRight size={16} />
          </Link>
        </div>
        <div className="feature-grid">
          {['Create', 'Support', 'Improve'].map((label, index) => (
            <article className="feature-card" key={label}>
              <span className="metric">{String(index + 1).padStart(2, '0')}</span>
              <h3>{label}</h3>
              <p>
                {[
                  'Launch focused products that solve practical problems with a clean, easy-to-understand experience.',
                  'Give customers straightforward paths for product access, questions, updates, and support.',
                  'Refine each brand through feedback, product learning, and steady improvements that make the tools more useful.'
                ][index]}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="section muted-band">
        <div className="container split-section">
          <div className="section-heading-padded">
            <span className="eyebrow">Whitestone ecosystem</span>
            <h2>AI education, API tools, and software support in one place.</h2>
            <p>
              Whitestone brings education, software, APIs, media, and support together so customers can find the right product path
              without sorting through disconnected sites.
            </p>
          </div>
          <div className="mini-grid">
            {[
              ['AI education and news', 'Courses, AI news, tool coverage, and practical guides for modern teams.', ecosystemDestinations[0]],
              ['API solutions', 'Useful API products for automation, documents, and customer workflows.', ecosystemDestinations[1]],
              ['Customer support', 'Clear paths for product questions, access requests, and help.', ecosystemDestinations[2]]
            ].map(([title, text, destination]) => {
              const Icon = destination.icon;
              return (
                <EcosystemLink className="mini-product" href={destination.href} external={destination.external} key={title}>
                  <Icon size={19} />
                  <span>{title}</span>
                  <small>{text}</small>
                </EcosystemLink>
              );
            })}
          </div>
        </div>
      </section>

      <ApiPreview />
      <LaunchSystem />
      <OperatingModel />
      <FocusAreas />
      <CtaBand />
    </>
  );
}

function TechConstellation() {
  return (
    <svg className="tech-constellation" viewBox="0 0 520 220" role="img" aria-label="Whitestone software network diagram">
      <defs>
        <linearGradient id="constellationStroke" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#58d5c9" />
          <stop offset="100%" stopColor="#8db4ff" />
        </linearGradient>
      </defs>
      <path d="M58 110 C132 28 210 164 270 94 S400 58 462 128" />
      <path d="M84 170 C166 112 208 40 310 122 S416 184 468 72" />
      {[58, 142, 232, 318, 402, 462].map((x, index) => (
        <g key={x}>
          <circle cx={x} cy={[110, 64, 152, 90, 146, 128][index]} r="14" />
          <circle cx={x} cy={[110, 64, 152, 90, 146, 128][index]} r="4" />
        </g>
      ))}
    </svg>
  );
}

function EcosystemLink({ className, href, external = false, children }) {
  if (external) {
    return (
      <a className={className} href={href}>
        {children}
      </a>
    );
  }

  return (
    <Link className={className} to={href}>
      {children}
    </Link>
  );
}

function LaunchSystem() {
  return (
    <section className="section container">
      <div className="launch-grid">
        <div>
          <span className="eyebrow">Built for dependable growth</span>
          <h2>A stronger foundation for every Whitestone product.</h2>
          <p>
            Whitestone is designed to grow as a coordinated technology company, not a loose collection of disconnected projects. Each
            product benefits from shared standards for support, product quality, customer communication, and long-term reliability.
          </p>
          <div className="hero-actions">
            <Link className="button primary" to="/ai">Publications</Link>
            <Link className="button secondary" to="/apis">API's</Link>
          </div>
        </div>
        <div className="signal-stack">
          {launchSignals.map(([title, text], index) => (
            <article className="signal-card" key={title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ApiPreview() {
  const apiLanes = [apiProducts[0], apiProducts[1], apiProducts[3]];

  return (
    <section className="section container">
      <div className="section-heading compact">
        <span className="eyebrow">API solutions</span>
        <h2>Developer tools built around practical business workflows.</h2>
      </div>
      <div className="product-grid">
        {apiLanes.map((apiProduct) => (
          <article className="product-card" key={apiProduct.slug}>
            <div className="product-topline">
              <span className="product-icon"><Code2 size={22} /></span>
              <span className="status live">Live</span>
            </div>
            <h3>{apiProduct.title}</h3>
            <p>{apiProduct.preview}</p>
            <div className="card-actions">
              <Link className="button small secondary" to={`/apis/${apiProduct.slug}`}>Learn More</Link>
            </div>
          </article>
        ))}
      </div>
      <div className="center-action">
        <Link className="button secondary" to="/apis">Explore API Solutions</Link>
      </div>
    </section>
  );
}

function OperatingModel() {
  return (
    <section className="section muted-band">
      <div className="container">
        <div className="section-heading">
          <span className="eyebrow">Operating standards</span>
          <h2>A trusted technology partner for customers who need modern tools that work.</h2>
          <p>
            Whitestone Computing LLC builds customer-ready software, AI resources, API products, and digital tools that help people move
            faster, make clearer decisions, and rely on technology with confidence.
          </p>
        </div>
        <div className="feature-grid">
          {operatingModel.map((item) => {
            const Icon = item.icon;
            return (
              <article className="feature-card elevated" key={item.title}>
                <span className="product-icon"><Icon size={22} /></span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FocusAreas() {
  return (
    <section className="section container">
      <div className="focus-layout">
        <div>
          <span className="eyebrow">Technology focus</span>
          <h2>Focused on useful products people can understand and trust.</h2>
        </div>
        <div className="focus-list">
          {focusAreas.map((item) => (
            <span key={item}>
              <CheckCircle2 size={18} /> {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="cta-band">
      <div className="container cta-content">
        <div>
          <span className="eyebrow">Work with Whitestone</span>
          <h2>Connect with Whitestone Computing about software, AI education, APIs, media, or product access.</h2>
        </div>
        <div className="hero-actions">
          <Link className="button primary" to="/work-with-us">Connect</Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <PageShell eyebrow="Our mission" title="Technology products built to save time, simplify work, and support real growth.">
      <div className="prose-grid">
        <article>
          <h2>Built for people who need technology to feel clear</h2>
          <p>
            Whitestone Computing creates software, AI resources, automation tools, document utilities, developer products, and technology
            media for people who want useful technology without the confusion. Every product is designed to help customers move faster,
            make cleaner decisions, and get important work done with less friction.
          </p>
          <p>
            The goal is simple: customers should understand what a product does, how it helps, and where to go next from the first visit.
            Whitestone focuses on clean experiences, practical features, helpful guidance, and dependable support across every brand it
            brings to market.
          </p>
        </article>
        <article>
          <h2>Practical tools with a clear reason to exist</h2>
          <p>
            Whitestone builds products around real customer needs: learning faster, creating documents, improving workflows, exploring
            AI, connecting with developer tools, managing hiring tasks, or staying informed through practical technology content. The
            company favors focused products that do their job well instead of bloated platforms that make simple work feel complicated.
          </p>
          <p>
            As the Whitestone portfolio grows, customers can expect the same standard across each product: simple onboarding, professional
            design, responsible data practices, clear communication, and support that is easy to find when it matters.
          </p>
        </article>
        <article>
          <h2>One company, many useful products</h2>
          <p>
            Whitestone works across AI education, AI-powered utilities, developer APIs, hiring software, document tools, productivity
            products, ecommerce experiences, and AI education. Each product has its own audience, but they share the same customer
            promise: make the next step easier, clearer, and more reliable.
          </p>
          <p>
            Some tools are built for everyday users. Others are made for developers, business owners, creators, operators, and growing
            teams. What connects them is a commitment to practical value, not feature clutter.
          </p>
        </article>
        <article>
          <h2>Designed to grow with customers</h2>
          <p>
            Whitestone is building for the long term. New products, education channels, API tools, media experiences, and workflow
            systems can be added over time while keeping the customer experience organized, polished, and easy to trust.
          </p>
          <p>
            That matters because customers should not have to chase scattered updates, unclear policies, or disconnected support paths.
            As Whitestone grows, the experience is meant to stay professional, useful, and easy to navigate.
          </p>
        </article>
      </div>
      <section className="mission-panel" id="mission">
        <div className="mission-copy">
          <span className="eyebrow">Our mission</span>
          <h2>Make useful technology easier to choose, easier to use, and easier to trust.</h2>
          <p>
            Whitestone exists to turn practical problems into clear digital products. That means building tools that help customers learn
            faster, organize work, create better documents, explore AI with confidence, and connect to software that feels dependable
            from the first interaction.
          </p>
          <p>
            The mission is not to overwhelm customers with endless features. It is to give them products that explain the value quickly,
            respect their time, and keep improving as their needs grow.
          </p>
          <div className="mission-points" aria-label="Whitestone mission priorities">
            {[
              ['Clarity first', 'Customers should know what a product does and why it matters before they have to ask.'],
              ['Practical outcomes', 'Every tool should help someone finish a real task, make a better decision, or move faster.'],
              ['Trust over noise', 'Whitestone prioritizes polished experiences, responsible support, and steady product quality.']
            ].map(([title, text]) => (
              <article key={title}>
                <CheckCircle2 size={18} />
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
        <div className="mission-visual" aria-label="Customer needs connected to Whitestone products">
          <svg viewBox="0 0 560 420" role="img" aria-labelledby="mission-visual-title">
            <title id="mission-visual-title">Whitestone mission flow</title>
            <defs>
              <linearGradient id="missionPanelGradient" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#e6f5f7" />
              </linearGradient>
              <linearGradient id="missionAccentGradient" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="#1f9bb4" />
                <stop offset="100%" stopColor="#5f7dff" />
              </linearGradient>
            </defs>
            <rect x="24" y="24" width="512" height="372" rx="24" fill="url(#missionPanelGradient)" stroke="rgba(43, 52, 64, 0.13)" />
            <path d="M164 126H262" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <path d="M164 210H234" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <path d="M164 294H262" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <path d="M336 126H430" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <path d="M360 210H430" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <path d="M336 294H430" stroke="#8ba0b6" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 10" />
            <rect x="56" y="86" width="128" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <rect x="56" y="170" width="128" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <rect x="56" y="254" width="128" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <text x="120" y="119" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Learn</text>
            <text x="120" y="142" textAnchor="middle" fill="#5d6876" fontSize="12">AI clearly</text>
            <text x="120" y="203" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Create</text>
            <text x="120" y="226" textAnchor="middle" fill="#5d6876" fontSize="12">documents</text>
            <text x="120" y="287" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Automate</text>
            <text x="120" y="310" textAnchor="middle" fill="#5d6876" fontSize="12">workflows</text>
            <rect x="222" y="112" width="116" height="196" rx="22" fill="#ffffff" stroke="rgba(31,155,180,0.3)" />
            <image href={whitestoneLogo} x="241" y="138" width="78" height="78" preserveAspectRatio="xMidYMid meet" />
            <text x="280" y="240" textAnchor="middle" fill="#161b22" fontSize="14" fontWeight="900">Whitestone</text>
            <text x="280" y="262" textAnchor="middle" fill="#5d6876" fontSize="13" fontWeight="800">Computing</text>
            <rect x="398" y="86" width="106" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <rect x="398" y="170" width="106" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <rect x="398" y="254" width="106" height="80" rx="14" fill="#ffffff" stroke="#d3dde8" />
            <text x="451" y="119" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Faster</text>
            <text x="451" y="142" textAnchor="middle" fill="#5d6876" fontSize="12">decisions</text>
            <text x="451" y="203" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Cleaner</text>
            <text x="451" y="226" textAnchor="middle" fill="#5d6876" fontSize="12">tools</text>
            <text x="451" y="287" textAnchor="middle" fill="#161b22" fontSize="15" fontWeight="800">Better</text>
            <text x="451" y="310" textAnchor="middle" fill="#5d6876" fontSize="12">support</text>
          </svg>
          <div className="mission-visual-caption">
            <strong>From need to useful product</strong>
            <span>Whitestone turns common digital friction into focused tools customers can understand and keep using.</span>
          </div>
        </div>
      </section>
      <div className="feature-grid about-principles">
        {[
          ['Easy to understand', 'Each product is shaped around clear benefits, simple next steps, and interfaces that help customers get started quickly.'],
          ['Ready to support', 'Customers get visible paths for questions, product access, account help, privacy requests, and general support.'],
          ['Built to keep improving', 'Whitestone listens for real customer needs, improves products over time, and keeps quality ahead of noise.']
        ].map(([title, text]) => (
          <article className="feature-card elevated" key={title}>
            <h3>{title}</h3>
            <p>{text}</p>
          </article>
        ))}
      </div>
      <section className="about-depth-panel">
        <span className="eyebrow">Customer promise</span>
        <h2>Useful technology should feel simple to start and worth coming back to.</h2>
        <p>
          Whether someone is learning AI, exploring APIs, creating documents, reviewing technology updates, or trying a Whitestone
          software product, the experience should feel polished, direct, and built around their time.
        </p>
        <p>
          Whitestone's promise is to build products that solve real problems, explain themselves clearly, improve with purpose, and give
          customers confidence in the tools they choose.
        </p>
      </section>
    </PageShell>
  );
}

function Products() {
  return (
    <PageShell eyebrow="Software division" title="Modern software products built for real business workflows.">
      <section className="api-doc-shell">
        <div>
          <span className="eyebrow">Software.whitestonecomputing.com</span>
          <h2>A growing portfolio of useful digital products.</h2>
          <p>
            Explore Whitestone software across AI utilities, document tools, productivity systems, ecommerce technology, and workflow
            platforms. Each brand is designed to help customers solve practical problems, move faster, and keep their digital work organized.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="https://software.whitestonecomputing.com">View All Software</a>
            <button className="button secondary" type="button" onClick={() => openNewsletterModal('software_updates_cta')}>Get Whitestone Updates</button>
          </div>
        </div>
        <div className="code-window" aria-label="Software portfolio categories">
          <span>AI utilities</span>
          <span>Document tools</span>
          <span>Payment documents</span>
          <span>Productivity and workflow software</span>
        </div>
      </section>
      <div className="product-grid expanded">
        {publicProducts.map((product) => (
          <ProductCard product={product} key={product.id} />
        ))}
      </div>
      <section className="pricing-panel">
        <div>
          <span className="eyebrow">Global plan levels</span>
          <h2>One shared tier model for every Whitestone product.</h2>
        </div>
        <div className="tier-grid">
          {['Free / Starter', 'Pro', 'Business'].map((tier) => (
            <div className="tier" key={tier}>
              <h3>{tier}</h3>
              <p>Mapped centrally for billing, user segmentation, analytics, and subscription reporting.</p>
            </div>
          ))}
        </div>
      </section>
      <LegalNoticeLinks scope="Software portfolio" basePath="/software" />
    </PageShell>
  );
}

function AISitePreview() {
  const tracks = [
    ['AI Courses', 'Structured learning paths for practical AI skills, from fundamentals to applied business workflows.', BookOpen],
    ['Tutorials And How-To Guides', 'Short lessons for prompt engineering, AI tools, automation, content workflows, and daily productivity.', Sparkles],
    ['AI For Business', 'Clean guidance for teams adopting AI without turning every process into a science project.', BriefcaseBusiness],
    ['Local AI And Self-Hosted AI', 'Resources for model selection, local-first experimentation, and practical AI workflows.', MonitorCheck]
  ];

  return (
    <PageShell eyebrow="ai.whitestonecomputing.com" title="AI Development & Education.">
      <section className="api-doc-shell">
        <div>
          <span className="eyebrow">AI education and media</span>
          <h2>AI learning, news, and practical guidance for modern teams.</h2>
          <p>
            Whitestone's AI hub brings courses, tutorials, tool coverage, model analysis, and business-focused guides into one place. It is
            built for people who want to understand AI, apply it confidently, and stay current without wading through noise.
          </p>
          <a className="button primary" href="https://ai.whitestonecomputing.com">Explore AI Hub</a>
        </div>
        <div className="code-window" aria-label="AI education site focus">
          <span>AI courses</span>
          <span>AI news and analysis</span>
          <span>Prompt engineering guides</span>
          <span>Automation tutorials</span>
        </div>
      </section>
      <div className="section-heading compact">
        <p>
          The Whitestone AI hub is the public home for AI education and AI news: courses, tutorials, how-to guides,
          prompt engineering, automation guides, model coverage, tool analysis, and practical AI innovation updates.
        </p>
        <div className="intro-actions">
          <a className="button primary" href="https://ai.whitestonecomputing.com">Explore AI Hub</a>
          <button className="button secondary" type="button" onClick={() => openNewsletterModal('ai_updates_cta')}>Get Whitestone Updates</button>
        </div>
      </div>
      <div className="feature-grid">
        {tracks.map(([title, text, Icon]) => (
          <a className="feature-card elevated linked-card" href="https://ai.whitestonecomputing.com" key={title}>
            <span className="product-icon"><Icon size={22} /></span>
            <h3>{title}</h3>
            <p>{text}</p>
            <span className="underlined-cta">
              Learn More <ArrowRight size={16} />
            </span>
          </a>
        ))}
      </div>
      <ArticleDeck />
      <div className="center-action">
        <a className="button primary" href="https://ai.whitestonecomputing.com">Explore AI Hub</a>
        <button className="button secondary" type="button" onClick={() => openNewsletterModal('ai_updates_cta')}>Get Whitestone Updates</button>
      </div>
      <LegalNoticeLinks scope="AI education and news site" basePath="/ai" />
    </PageShell>
  );
}

function APISitePreview() {
  return (
    <PageShell eyebrow="api.whitestonecomputing.com" title="Developer tools and API products from Whitestone Computing.">
      <div className="api-doc-shell">
        <div>
          <span className="eyebrow">Customer-ready API platform</span>
          <h2>API products that make automation easier to ship.</h2>
          <p>
            Explore customer-ready APIs for AI utilities, document automation, SaaS operations, and business workflows. Whitestone gives
            teams a clearer path to product access, documentation, implementation support, and reliable automation customers can use.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="https://api.whitestonecomputing.com">Explore API Products</a>
            <button className="button secondary" type="button" onClick={() => openNewsletterModal('developer_updates_cta')}>Request Developer Updates</button>
          </div>
        </div>
        <div className="code-window" aria-label="API documentation shell">
          <span>GET /v1/tools</span>
          <span>POST /v1/document-jobs</span>
          <span>POST /v1/automation-events</span>
          <span>Authorization: Bearer {'{API_KEY}'}</span>
        </div>
      </div>
      <div className="product-grid expanded">
        {apiProducts.map((apiProduct) => (
          <article className="product-card" key={apiProduct.slug}>
            <div className="product-topline">
              <span className="product-icon"><Code2 size={22} /></span>
              <span className="status live">Live</span>
            </div>
            <h3>{apiProduct.title}</h3>
            <p>{apiProduct.preview}</p>
            <div className="card-actions">
              <Link className="button small secondary" to={`/apis/${apiProduct.slug}`}>Learn More</Link>
            </div>
          </article>
        ))}
      </div>
      <LegalNoticeLinks scope="Developer API platform" basePath="/apis" />
    </PageShell>
  );
}

function APIDetailPage() {
  const { apiSlug } = useParams();
  const apiProduct = apiProducts.find((item) => item.slug === apiSlug);

  if (!apiProduct) return <NotFound />;

  return (
    <PageShell eyebrow="Whitestone API product" title={apiProduct.title}>
      <section className="api-doc-shell">
        <div>
          <span className="eyebrow">Live API lane</span>
          <h2>{apiProduct.preview}</h2>
          <p>{apiProduct.overview}</p>
          <div className="hero-actions">
            <a className="button primary" href="https://api.whitestonecomputing.com">Open API Site</a>
            <button className="button secondary" type="button" onClick={() => openNewsletterModal('developer_updates_cta')}>Request Access</button>
          </div>
        </div>
        <div className="code-window" aria-label={`${apiProduct.title} example endpoints`}>
          {apiProduct.endpoints.map((endpoint) => (
            <span key={endpoint}>{endpoint}</span>
          ))}
          <span>Status: Live</span>
        </div>
      </section>
      <section className="section compact-section">
        <div className="section-heading compact">
          <span className="eyebrow">Customer value</span>
          <h2>Built for practical implementation, not abstract API catalogs.</h2>
        </div>
        <div className="feature-grid">
          {apiProduct.valuePoints.map((point, index) => (
            <article className="feature-card elevated" key={point}>
              <span className="metric">{String(index + 1).padStart(2, '0')}</span>
              <p>{point}</p>
            </article>
          ))}
        </div>
      </section>
      <div className="center-action">
        <Link className="button secondary" to="/apis">Back to All APIs</Link>
      </div>
      <LegalNoticeLinks scope={apiProduct.title} basePath="/apis" />
    </PageShell>
  );
}

function ArticleDeck() {
  const articles = [
    ['Practical AI adoption starts with smaller workflows', 'AI Education', 'May 2026'],
    ['How Whitestone organizes public software products', 'Software', 'May 2026'],
    ['Designing API products around practical developer needs', 'API', 'May 2026']
  ];

  return (
    <div className="article-grid">
      {articles.map(([title, category, date]) => (
        <article className="article-card" key={title}>
          <span>{category}</span>
          <h3>{title}</h3>
          <p>{date} / Whitestone Computing editorial desk</p>
        </article>
      ))}
    </div>
  );
}

function NewsletterModal({ open, sourceForm, sourcePage, onClose }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });
  const updateTopics = [
    ['AI education', BookOpen],
    ['New APIs', Code2],
    ['Software releases', Rocket],
    ['AI news', Sparkles]
  ];

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose('dismissed');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) setState({ status: 'idle', message: '' });
  }, [open]);

  if (!open) return null;

  async function subscribe(event) {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!isValidEmailAddress(normalized)) {
      setState({ status: 'error', message: 'Enter a valid email address.' });
      return;
    }
    setState({ status: 'loading', message: '' });
    try {
      await submitForm('/api/newsletter/subscribe', {
        email: normalized,
        sourceSite: window.location.hostname || 'whitestonecomputing.com',
        sourcePage,
        sourceForm,
        consentType: sourceForm?.includes('developer') || sourcePage === '/apis' ? 'developer_updates' : 'newsletter'
      });
      setState({
        status: 'success',
        message: "You're on the list. We'll send useful Whitestone updates to your inbox."
      });
      window.setTimeout(() => onClose('subscribed'), 900);
    } catch (error) {
      const already = error.message.toLowerCase().includes('already');
      setState({
        status: already ? 'success' : 'error',
        message: already ? "You're already on the Whitestone updates list." : 'Temporary server error. Please try again in a moment.'
      });
      if (already) window.setTimeout(() => onClose('subscribed'), 900);
    }
  }

  return (
    <div className="newsletter-backdrop" role="presentation" onMouseDown={() => onClose('dismissed')}>
      <section
        className="newsletter-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="newsletter-close" type="button" aria-label="Close updates signup" onClick={() => onClose('dismissed')}>
          <X size={18} />
        </button>
        <span className="newsletter-kicker">Whitestone updates</span>
        <h2 id="newsletter-title">Stay Updated with Whitestone</h2>
        <p>
          Get product updates, AI education drops, new API releases, software tools, developer notes,
            and AI news updates from Whitestone Computing.
        </p>
        <div className="newsletter-highlights" aria-label="Update topics">
          {updateTopics.map(([topic, Icon]) => (
            <span key={topic}>
              <Icon size={15} />
              {topic}
            </span>
          ))}
        </div>
        <form className="newsletter-form" onSubmit={subscribe}>
          <label>
            <span>Email address</span>
            <input
              autoFocus
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              maxLength={180}
              pattern="[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}"
              title="Enter a valid email address, like name@example.com."
              aria-invalid={state.status === 'error'}
              placeholder="Email address"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={state.status === 'loading' || state.status === 'success'}
            />
          </label>
          <button className="button primary" type="submit" disabled={state.status === 'loading' || state.status === 'success'}>
            {state.status === 'loading' ? 'Joining...' : 'Join Developer Updates'}
          </button>
        </form>
        <small className={`newsletter-message ${state.status === 'error' ? 'error' : state.status === 'success' ? 'success' : ''}`}>
          {state.message || 'Consent-based updates only. Every email includes an unsubscribe option.'}
        </small>
      </section>
    </div>
  );
}

function WorkWithUs() {
  const location = useLocation();
  const selectedProduct = new URLSearchParams(location.search).get('product');
  const productInterest = publicProducts.some((product) => product.id === selectedProduct) ? selectedProduct : 'aibackgroundremover';

  return (
    <PageShell eyebrow="Work with us" title="Partner, request access, or follow Whitestone product updates.">
      <div className="prose-grid">
        <article>
          <h2>Software and SaaS partnerships</h2>
          <p>Contact Whitestone about lightweight tools, API ideas, automation products, software ventures, or product integrations.</p>
        </article>
        <article>
          <h2>AI education and media</h2>
          <p>Use this lane for AI course ideas, tutorials, article pitches, practical guides, and educational product requests.</p>
        </article>
        <article>
          <h2>Developer access</h2>
          <p>Request updates about Whitestone API products, developer tools, software workflows, and early product access.</p>
        </article>
        <article>
          <h2>Product access and updates</h2>
          <p>Tell us what you are building or what Whitestone product you want to use, and we will route the request to the right lane.</p>
          <Link className="button secondary" to="/software">Explore Software</Link>
        </article>
      </div>
      <div className="intake-grid">
        <section>
          <span className="eyebrow">General inquiry</span>
          <h2>Talk to Whitestone.</h2>
          <FormPanel
            type="contact"
            endpoint="/api/contact"
            fields={[
              ['name', 'Name', 'text'],
              ['email', 'Email', 'email'],
              ['company', 'Company', 'text'],
              ['productInterest', 'Product interest', 'select'],
              ['message', 'Message', 'textarea']
            ]}
            initial={{ productInterest: 'general' }}
            submitLabel="Send Work Request"
          />
        </section>
        <section>
          <span className="eyebrow">Product access</span>
          <h2>Request access to a Whitestone product.</h2>
          <FormPanel
            type="invite"
            endpoint="/api/invite"
            fields={[
              ['fullName', 'Full name', 'text'],
              ['email', 'Email', 'email'],
              ['company', 'Company / Organization', 'text'],
              ['productInterest', 'Product interested in', 'select'],
              ['role', 'Role / use case', 'text'],
              ['reason', 'Reason for access', 'textarea']
            ]}
            initial={{ productInterest }}
            submitLabel="Submit Access Request"
          />
        </section>
      </div>
    </PageShell>
  );
}

function Copyright() {
  return (
    <LegalPage title="Trademarks, Copyright, And Proprietary Rights">
      <p>
        This notice applies to Whitestone Computing LLC, Whitestone Computing, whitestonecomputing.com, company-operated subdomains,
        public pages, software products, SaaS tools, APIs, dashboards, educational materials, media properties, documentation, designs,
        workflows, business systems, and related digital properties operated by Whitestone Computing LLC.
      </p>
      <h2>Ownership Of Whitestone Assets</h2>
      <p>
        Whitestone Computing LLC owns or controls the Whitestone Computing name, WC mark, company branding, product names, product
        concepts, interface designs, page layouts, copy, graphics, logos, icons, documentation, software code, source files, compiled
        assets, backend systems, databases, product workflows, automation flows, API structures, admin tools, internal operating systems,
        launch materials, training materials, support materials, business methods, and proprietary product direction unless otherwise
        stated in writing.
      </p>
      <p>
        These materials may be protected by copyright, trademark, service mark, trade dress, trade secret, contract, unfair competition,
        database, and other intellectual property or proprietary-rights laws where applicable. All rights not expressly granted in writing
        are reserved by Whitestone Computing LLC.
      </p>
      <h2>Software, Products, And Business Innovations</h2>
      <p>
        Whitestone products, software utilities, SaaS concepts, product architecture, business workflows, automation systems, reusable
        software modules, API concepts, educational systems, dashboards, data structures, configuration patterns, support systems, and
        internal operating processes are proprietary to Whitestone Computing LLC unless a separate written license, open-source notice, or
        third-party agreement says otherwise.
      </p>
      <p>
        You may not copy, clone, scrape, reverse engineer, decompile, resell, sublicense, repackage, host, mirror, frame, impersonate,
        commercially exploit, or create confusingly similar services from Whitestone products, interfaces, code, documentation, workflows,
        business logic, product names, branding, or protected content except where allowed by law or by a written agreement signed by
        Whitestone Computing LLC.
      </p>
      <h2>Brand Names, Marks, And Trade Dress</h2>
      <p>
        Whitestone Computing LLC, Whitestone Computing, WC, company logos, product names, software names, slogans, interface styling,
        visual identity, site structure, and brand presentation may function as trademarks, service marks, trade names, or trade dress of
        Whitestone Computing LLC. Use of Whitestone names or marks must not suggest sponsorship, endorsement, partnership, affiliation,
        approval, or ownership unless Whitestone gives prior written permission.
      </p>
      <p>
        You may not register domains, social handles, app names, company names, advertisements, search ads, marketplace listings, or
        software packages that use Whitestone marks, misspellings, confusingly similar names, or brand presentation in a way that could
        mislead customers or interfere with Whitestone's business.
      </p>
      <h2>Content, Media, And AI-Related Materials</h2>
      <p>
        Articles, lessons, AI education materials, prompt examples, tutorials, product announcements, screenshots, videos, downloads,
        templates, reports, generated examples, edited outputs, design systems, and other content published by Whitestone are protected
        materials unless clearly marked otherwise. Limited personal viewing or ordinary website use does not transfer ownership or grant a
        commercial reuse license.
      </p>
      <p>
        AI-assisted content, automation examples, sample outputs, and technical demonstrations are provided for informational and product
        experience purposes. They may not be copied into competing products, represented as your own Whitestone-equivalent system, or used
        to train, benchmark, market, or operate a competing service without written permission where permission is required by law or
        contract.
      </p>
      <h2>No Implied License</h2>
      <p>
        Access to a Whitestone website, product, demo, API, dashboard, document, download, or communication does not grant ownership,
        title, source-code access, resale rights, redistribution rights, white-label rights, data extraction rights, model-training rights,
        or permission to use Whitestone intellectual property beyond the limited access needed to use the service as intended.
      </p>
      <p>
        Any license, integration right, partner right, reseller right, API access right, beta access right, or commercial use right must be
        granted in a separate written agreement or published product terms. Whitestone may revoke access or permission for misuse, brand
        confusion, scraping, security risk, infringement, abuse, or violation of applicable terms.
      </p>
      <h2>Third-Party Materials</h2>
      <p>
        Third-party names, logos, tools, libraries, platforms, APIs, services, trademarks, copyrighted works, and other materials remain
        the property of their respective owners. References to third-party products or services are for identification, compatibility,
        integration, commentary, or informational purposes and do not imply endorsement, sponsorship, or ownership by Whitestone unless
        expressly stated.
      </p>
      <h2>Feedback And Submissions</h2>
      <p>
        If you send feedback, ideas, feature requests, bug reports, business suggestions, product concepts, workflow suggestions, or other
        submissions to Whitestone, you grant Whitestone permission to review, use, develop, improve, commercialize, and incorporate those
        submissions without compensation or restriction, unless a separate written agreement says otherwise. Do not submit confidential
        ideas, trade secrets, or proprietary materials unless a written agreement is already in place.
      </p>
      <h2>Enforcement And Contact</h2>
      <p>
        Whitestone reserves the right to investigate misuse, infringement, impersonation, unauthorized copying, scraping, brand confusion,
        unauthorized resale, unauthorized API use, or other activity that harms Whitestone, its customers, its products, or its business
        interests. Whitestone may request removal, suspend access, restrict accounts, pursue platform complaints, send takedown notices,
        or seek other remedies available by law.
      </p>
      <p>
        Questions about brand use, legal notices, copyright concerns, permission requests, or suspected infringement may be sent to
        {' '}<a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
      </p>
    </LegalPage>
  );
}

function OAuthResult({ success = false }) {
  return (
    <PageShell eyebrow="Google OAuth" title={success ? 'Google sign-in route completed.' : 'Google sign-in could not be completed.'}>
      <article className="legal-copy">
        <p>
          {success
            ? 'The public OAuth callback route is reachable and ready for the real credential exchange layer when production credentials are configured.'
            : 'The sign-in attempt was cancelled, failed validation, or OAuth environment variables are not configured yet.'}
        </p>
        <Link className="button primary" to="/work-with-us">Return To Work With Us</Link>
      </article>
    </PageShell>
  );
}

function ProductCard({ product }) {
  const Icon = product.icon;
  return (
    <article className="product-card">
      <div className="product-topline">
        <span className="product-icon"><Icon size={22} /></span>
        <span className={`status ${product.status.toLowerCase()}`}>{product.status}</span>
      </div>
      <h3>{product.name}</h3>
      <p>{product.description}</p>
      <div className="card-meta">
        <span>{product.category}</span>
      </div>
      <div className="card-actions">
        <a className="button small secondary" href={product.websiteUrl} target="_blank" rel="noreferrer">Learn More</a>
      </div>
    </article>
  );
}

function Contact() {
  return (
    <PageShell eyebrow="Contact" title="Let's build something useful together.">
      <FormPanel
        type="contact"
        endpoint="/api/contact"
        fields={[
          ['name', 'Name', 'text'],
          ['email', 'Email', 'email'],
          ['company', 'Company', 'text'],
          ['productInterest', 'Product interest', 'select'],
          ['message', 'Message', 'textarea']
        ]}
        initial={{ productInterest: 'general' }}
        submitLabel="Submit Contact Request"
      />
      <ContactAside />
    </PageShell>
  );
}

function InviteRedirect() {
  const location = useLocation();
  return <Navigate to={`/work-with-us${location.search}`} replace />;
}

function Support() {
  return (
    <PageShell eyebrow="Support" title="Product support for the Whitestone software portfolio.">
      <div className="support-strip">
        {['Account access', 'Billing', 'Product issue', 'Data or privacy', 'Partnership'].map((item) => (
          <span key={item}><Ticket size={17} /> {item}</span>
        ))}
      </div>
      <FormPanel
        type="support"
        endpoint="/api/support"
        fields={[
          ['name', 'Name', 'text'],
          ['email', 'Email', 'email'],
          ['product', 'Product', 'select'],
          ['category', 'Category', 'supportCategory'],
          ['priority', 'Priority', 'priority'],
          ['issue', 'Issue description', 'textarea']
        ]}
        initial={{ product: 'none', category: 'Technical support', priority: 'Normal' }}
        submitLabel="Submit Support Request"
      />
      <ContactAside />
    </PageShell>
  );
}

function productOptionLabel(productId) {
  return supportProductOptions.find((product) => product.id === productId)?.name || productId || 'None / General';
}

function FormPanel({ endpoint, fields, initial, submitLabel }) {
  const [values, setValues] = useState(initial || {});
  const [state, setState] = useState({ status: 'idle', message: '' });
  const allowGeneralProduct = endpoint === '/api/contact';
  const isSupportForm = endpoint === '/api/support';

  async function onSubmit(event) {
    event.preventDefault();
    setState({ status: 'loading', message: '' });
    try {
      await submitForm(endpoint, values);
      setState({ status: 'success', message: 'Submitted successfully. Whitestone Computing LLC will review it.' });
      setValues(initial || {});
    } catch (error) {
      setState({ status: 'error', message: error.message });
    }
  }

  return (
    <form className="form-panel" onSubmit={onSubmit}>
      {fields.map(([name, label, type]) => (
        <label className={type === 'textarea' ? 'field wide' : 'field'} key={name}>
          <span>{label}</span>
          {type === 'textarea' ? (
            <textarea required value={values[name] || ''} onChange={(event) => setValues({ ...values, [name]: event.target.value })} />
          ) : type === 'select' ? (
            <select required value={values[name] || ''} onChange={(event) => setValues({ ...values, [name]: event.target.value })}>
              {name === 'productInterest' && allowGeneralProduct && <option value="general">General</option>}
              {(name === 'product' && isSupportForm ? supportProductOptions : productOptions).map((product) => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </select>
          ) : type === 'priority' ? (
            <select required value={values[name] || 'Normal'} onChange={(event) => setValues({ ...values, [name]: event.target.value })}>
              {['Low', 'Normal', 'High', 'Urgent'].map((priority) => (
                <option key={priority}>{priority}</option>
              ))}
            </select>
          ) : type === 'supportCategory' ? (
            <select required value={values[name] || 'Technical support'} onChange={(event) => setValues({ ...values, [name]: event.target.value })}>
              {['Technical support', 'Billing', 'Refund review', 'Account access', 'Abuse report', 'Data or privacy', 'Partnership'].map((category) => (
                <option key={category}>{category}</option>
              ))}
            </select>
          ) : (
            <input required={name !== 'company'} type={type} value={values[name] || ''} onChange={(event) => setValues({ ...values, [name]: event.target.value })} />
          )}
        </label>
      ))}
      <button className="button primary" type="submit" disabled={state.status === 'loading'}>
        {state.status === 'loading' ? 'Submitting...' : submitLabel}
      </button>
      {state.message && <p className={`form-message ${state.status}`}>{state.message}</p>}
    </form>
  );
}

function ContactAside() {
  return (
    <aside className="contact-aside">
      <Mail size={22} />
      <h2>Support email</h2>
      <p>For product questions, business inquiries, support, and privacy requests, contact:</p>
      <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
    </aside>
  );
}

function Privacy() {
  return (
    <LegalPage title="Privacy Policy">
      <p><strong>Last updated: May 2026.</strong> This Privacy Policy explains how Whitestone Computing LLC collects, uses, discloses, retains, and protects information across Whitestone Computing websites, public service pages, API pages, education content, software products, support channels, invite flows, account systems, administrative systems, and portfolio brands operated under the Whitestone Computing umbrella.</p>
      <p>
        This policy is intended to provide transparent notice for users in the United States and, where applicable, users in jurisdictions
        with privacy laws such as the GDPR, UK GDPR, CCPA/CPRA, and similar state privacy laws. It is not a promise that every product
        collects every category below; actual collection depends on which Whitestone site, product, API, or form you use.
      </p>
      <h2>Company-Wide Scope</h2>
      <p>
        This policy applies to Whitestone Computing LLC and to company-operated pages, sub-sites, product previews, SaaS products,
        education lanes, AI news pages, API documentation, invite forms, contact forms, support desks, administrative dashboards,
        analytics systems, and future product brands that link to this policy. Examples include Whitestone Computing pages for AI
        education, developer APIs, software portfolio products, news and media, operating infrastructure, and related services.
      </p>
      <p>
        Some Whitestone products may publish additional product-specific notices, payment terms, cookie notices, data processing terms,
        or security documentation. If a product-specific notice conflicts with this policy for that product, the more specific notice
        controls for that product to the extent of the conflict.
      </p>
      <h2>Newsletter And Developer Updates</h2>
      <p>
        Whitestone may collect an email address when someone chooses to join newsletter, developer-update, AI education, software
        release, or AI news updates. These signups are consent-based. Whitestone stores the signup source, page, form,
        consent timestamp, subscription status, and unsubscribe status so communications can be handled responsibly.
      </p>
      <p>
        Account, support, contact, and product-access emails may be retained for transactional service reasons, but they are not treated
        as marketing subscribers unless the person has opted into updates. Marketing emails include an unsubscribe option, and
        unsubscribed addresses are excluded from future marketing campaigns.
      </p>
      <h2>Role Of Whitestone And Service Providers</h2>
      <p>
        Whitestone may act as a business, controller, service provider, processor, or product operator depending on the product and the
        context. Vendors that process information for hosting, authentication, email, payments, analytics, support, logging, storage,
        infrastructure, automation, and security generally process information on Whitestone's behalf under their own contractual,
        technical, and legal frameworks.
      </p>
      <h2>Information We Collect</h2>
      <p>
        We may collect contact information, account information, organization details, product interest, support messages, invite
        requests, user-generated form content, API usage metadata, device and browser information, IP address, approximate location,
        authentication metadata, billing metadata, payment status, subscription tier, product events, audit logs, security logs, and
        cookie or similar tracking information.
      </p>
      <p>
        Depending on the product, we may also process uploaded documents, images, prompts, generated outputs, job configuration,
        automation settings, API request and response metadata, article engagement signals, learning progress, product preferences,
        diagnostic reports, abuse reports, consent records, communication history, and internal admin notes used to support or operate
        the service.
      </p>
      <p>
        We generally do not ask for highly sensitive personal information unless a specific product workflow requires it. Users should
        avoid submitting Social Security numbers, government identification numbers, health information, financial account credentials,
        children's information, or other sensitive data unless a product specifically requests it and provides an appropriate notice.
      </p>
      <p>
        We may receive information directly from you, automatically from your device or product usage, from payment processors,
        authentication providers, analytics providers, email providers, hosting providers, customer support tooling, and other service
        providers used to operate Whitestone services.
      </p>
      <h2>Sources Of Information</h2>
      <p>
        Information may come from forms you submit, account creation, OAuth sign-in, product usage, API calls, browser and device signals,
        cookies or local storage, payment processors, email interactions, support messages, invite requests, admin actions, security
        monitoring, analytics tools, product integrations, and service providers that help operate Whitestone systems.
      </p>
      <h2>How We Use Information</h2>
      <p>
        We use information to provide and secure services, operate websites and APIs, process forms and support requests, manage accounts,
        authenticate users, process payments and subscriptions, prevent fraud and abuse, debug and improve products, measure usage,
        communicate with users, send administrative notices, maintain audit logs, comply with legal obligations, and evaluate future
        product demand.
      </p>
      <p>
        We may also use information to route requests to the correct product team, personalize product settings, maintain user sessions,
        enforce rate limits, protect admin dashboards, review product readiness, improve documentation, analyze feature demand, detect
        suspicious activity, investigate policy violations, perform internal reporting, and prepare aggregated or de-identified business
        insights across the Whitestone portfolio.
      </p>
      <h2>AI, Automation, Document, And API Data</h2>
      <p>
        Whitestone products may include AI education, AI utilities, document tools, developer APIs, automation systems, and content
        workflows. User prompts, uploaded content, generated outputs, document job metadata, API request metadata, and workflow settings
        may be processed to provide the requested service, secure the platform, troubleshoot errors, enforce usage limits, and improve
        product reliability. Where a third-party AI, infrastructure, or processing provider is used, data may be processed by that provider
        according to its applicable terms and privacy commitments.
      </p>
      <p>
        Users are responsible for ensuring they have the right to submit data to Whitestone products and should not upload confidential,
        regulated, or sensitive data unless the specific product is designed and documented for that use.
      </p>
      <h2>Payments, OAuth, Email, And Third-Party Services</h2>
      <p>
        Payment workflows may be handled by processors such as Stripe. Google OAuth or other authentication providers may be used for
        sign-in. Email, hosting, analytics, logging, storage, customer support, and automation providers may process information on our
        behalf. We do not intentionally expose private API keys, OAuth secrets, Stripe secrets, or email provider secrets in frontend code.
      </p>
      <p>
        Whitestone does not generally receive full payment card numbers when a payment processor is used. Payment processors may provide
        billing status, customer identifiers, transaction metadata, subscription state, invoice records, dispute records, and limited card
        details such as brand, expiration, and last four digits where needed for receipts, support, accounting, or fraud prevention.
      </p>
      <h2>Cookies And Analytics</h2>
      <p>
        Whitestone websites may use cookies, local storage, pixels, analytics events, and similar technologies for session management,
        security, preferences, performance, fraud prevention, product analytics, and measurement. Where required, non-essential cookies
        should be controlled through consent settings or browser controls. Some browsers provide Global Privacy Control or similar
        signals; Whitestone should honor legally required opt-out signals when implemented in production.
      </p>
      <h2>De-Identified And Aggregated Information</h2>
      <p>
        Whitestone may use aggregated, anonymized, or de-identified information for analytics, product planning, reporting, security
        analysis, business forecasting, and public or internal metrics. Whitestone does not attempt to re-identify de-identified
        information except as permitted by law, such as to test whether de-identification controls remain effective.
      </p>
      <h2>Sharing And Disclosure</h2>
      <p>
        We may disclose information to service providers, payment processors, authentication providers, hosting providers, legal or
        compliance advisors, product operators under the Whitestone umbrella, law enforcement or regulators when legally required, and
        successor entities in connection with a merger, acquisition, financing, restructuring, or sale of assets. We do not sell personal
        information for money. If future analytics or advertising tools are considered a "sale" or "sharing" under applicable law, users
        should be provided the legally required opt-out choices.
      </p>
      <p>
        We may also disclose information to protect rights, safety, and security; investigate fraud or abuse; enforce terms; respond to
        subpoenas or lawful requests; complete transactions requested by users; integrate product services; or support a business transfer
        involving some or all Whitestone assets, products, equity, or operations.
      </p>
      <h2>California And State Privacy Notice</h2>
      <p>
        Depending on the product and user interaction, Whitestone may collect categories of personal information such as identifiers,
        commercial information, internet or network activity, approximate geolocation, professional or organization information, payment
        and subscription metadata, user-generated content, support communications, and inferences drawn from product activity. We collect
        these categories for the business and commercial purposes described in this policy, retain them as described below, and disclose
        them to the categories of recipients described in this policy.
      </p>
      <p>
        Whitestone does not knowingly sell personal information for money. If a future analytics, advertising, or cross-context tracking
        tool is treated as "selling" or "sharing" personal information under applicable state law, Whitestone should provide required
        notices and opt-out mechanisms. California and similar state residents may have rights to know, access, correct, delete, opt out,
        limit certain sensitive information uses, and avoid discrimination for exercising privacy rights, subject to legal exceptions.
      </p>
      <h2>User Rights And Choices</h2>
      <p>
        Depending on where you live, you may have rights to request access, correction, deletion, portability, restriction, objection,
        withdrawal of consent, or opt-out of certain processing. California and similar state privacy laws may provide rights to know,
        delete, correct, opt out of sale or sharing, limit use of sensitive personal information, and non-discrimination for exercising
        privacy rights. EEA and UK users may have GDPR rights and may contact a supervisory authority.
      </p>
      <p>
        To make a privacy request, contact {supportEmail}. We may need to verify your identity before acting on a request. We may deny
        or limit requests where permitted by law, including for security, fraud prevention, legal compliance, accounting, dispute
        resolution, or when retaining information is necessary to provide the service.
      </p>
      <h2>Marketing And Communications Choices</h2>
      <p>
        Whitestone may send administrative messages, support replies, security notices, product updates, invite responses, billing notices,
        and service communications. Where marketing emails or newsletters are offered, users should be able to unsubscribe or opt out
        through the email instructions or by contacting {supportEmail}. Transactional, legal, security, and account-related notices may
        still be sent where needed to operate the service.
      </p>
      <h2>Retention</h2>
      <p>
        We retain information only as long as reasonably needed for the purposes described in this policy, including account operation,
        support, audits, security, legal compliance, tax and accounting, dispute resolution, backups, and product analytics. Retention
        periods may vary by product, data type, legal requirement, and operational need.
      </p>
      <p>
        Examples of retention factors include whether an account remains active, whether data is needed for billing records, whether a
        support issue is open, whether logs are needed for security review, whether backups are still cycling out, whether a legal hold
        applies, and whether records are needed for tax, accounting, audit, dispute, abuse-prevention, or compliance purposes.
      </p>
      <h2>Data Protection And Security</h2>
      <p>
        Whitestone uses administrative, technical, and organizational safeguards appropriate to the nature of the data, including access
        controls, environment-based secret management, server-side validation, rate limiting, audit logging, secure cookies where
        applicable, and separation of public frontend code from private credentials. No online service can be guaranteed completely
        secure, and users are responsible for maintaining secure credentials and authorized account access.
      </p>
      <p>
        Public sites, product frontends, admin dashboards, private provider credentials, payment integrations, OAuth settings, API keys,
        and operational logs should remain separated. Production systems should use HTTPS, strong session secrets, hashed passwords,
        least-privilege provider accounts, protected admin routes, dependency updates, backups, incident response procedures, and access
        review appropriate to the product's risk level.
      </p>
      <h2>Data Transfers</h2>
      <p>
        Whitestone is based in the United States. If information is processed outside your jurisdiction, it may be subject to the laws of
        the country where it is processed. Where legally required, Whitestone should use appropriate safeguards for international transfers,
        such as provider contractual commitments, data processing terms, or other lawful transfer mechanisms.
      </p>
      <h2>Children</h2>
      <p>
        Whitestone services are not directed to children under 13, and we do not knowingly collect personal information from children
        under 13. If you believe a child provided information to us, contact {supportEmail} so we can review and delete it where required.
      </p>
      <h2>International Users</h2>
      <p>
        Whitestone is operated from the United States. If you access the services from outside the United States, your information may be
        processed in the United States or other countries where service providers operate, which may have different data protection laws.
      </p>
      <h2>Changes And Contact</h2>
      <p>
        We may update this policy as products, laws, providers, or processing practices change. The updated version will be posted on
        the site with a revised date. Privacy questions and requests may be sent to {supportEmail}.
      </p>
    </LegalPage>
  );
}

function Terms() {
  return (
    <LegalPage title="Terms of Service">
      <p><strong>Last updated: May 2026.</strong> These Terms of Service govern access to and use of Whitestone Computing LLC websites, public sub-sites, API pages, education content, SaaS products, software tools, support forms, invite flows, account features, administrative systems, media pages, and related services operated under the Whitestone Computing umbrella.</p>
      <p>
        By using Whitestone services, you agree to these Terms. If you use the services for a company or organization, you represent
        that you have authority to bind that organization. If you do not agree, do not use the services.
      </p>
      <h2>Portfolio-Wide Coverage</h2>
      <p>
        These Terms apply to Whitestone Computing LLC and to company-operated products, previews, sub-sites, landing pages, software
        products, AI education pages, AI news pages, developer API pages, support forms, invite forms, administrative dashboards, and
        future product brands that link to these Terms. Product-specific terms, subscription terms, API terms, data processing terms, or
        written order forms may add to or modify these Terms for a specific product.
      </p>
      <h2>Important Notices</h2>
      <p>
        Whitestone services may include educational content, technology commentary, AI-related materials, automation tools, document
        utilities, software workflows, and beta product previews. They are not legal, tax, accounting, medical, financial, employment,
        security, or other regulated professional advice. Users are responsible for independent review before relying on outputs,
        documents, recommendations, generated content, or workflow suggestions.
      </p>
      <h2>Accounts And Access</h2>
      <p>
        You are responsible for accurate account information, maintaining credential security, all activity under your account, and
        ensuring that your use is authorized and lawful. Whitestone may provide free, invite-only, beta, paid, subscription, internal,
        API, or limited-access services and may modify eligibility or access requirements at any time.
      </p>
      <p>
        You must not share credentials, API keys, admin sessions, OAuth access, or restricted product links with unauthorized users. If
        you believe an account, key, password, or session has been compromised, you should stop using the affected credential and contact
        Whitestone promptly.
      </p>
      <h2>Acceptable Use</h2>
      <p>
        You may not misuse the services, violate laws, infringe rights, submit unlawful or harmful content, attempt unauthorized access,
        bypass security, scrape or overload systems, reverse engineer protected technology except where legally permitted, interfere with
        service operation, abuse support channels, impersonate others, send spam, commit fraud, or use Whitestone services to create,
        distribute, or facilitate malware, harassment, discrimination, exploitation, or deceptive activity.
      </p>
      <p>
        You also may not use Whitestone services to process data you are not authorized to use, violate privacy or publicity rights,
        generate deceptive identities or misleading content, perform credential attacks, test security without permission, evade rate
        limits, resell access without authorization, remove legal notices, or use product outputs in regulated decisions without the
        required human review, professional review, and legal basis.
      </p>
      <h2>APIs, Automation, And Developer Use</h2>
      <p>
        API access may be subject to keys, rate limits, quotas, usage rules, logging, monitoring, and product-specific terms. You may not
        share API keys, hide abusive traffic, use APIs to attack systems, or use outputs in a way that violates law or third-party rights.
        Whitestone may suspend API access for security, abuse, nonpayment, excessive load, policy violations, or operational risk.
      </p>
      <p>
        Developer documentation, sample endpoints, API examples, and public platform pages may describe planned or experimental products.
        Availability, pricing, quotas, authentication requirements, schemas, SDKs, service-level commitments, and endpoint behavior may
        change before or after launch.
      </p>
      <h2>Content, AI, And User Materials</h2>
      <p>
        You retain ownership of materials you submit, subject to rights needed for Whitestone to operate, secure, support, process, and
        improve the services. You are responsible for ensuring you have rights to upload or process any content. AI, automation, document,
        API, or software outputs may be inaccurate, incomplete, or unsuitable for regulated decisions without human review.
      </p>
      <p>
        You grant Whitestone a limited right to host, process, transmit, display, reproduce, transform, and use submitted materials and
        outputs as necessary to provide the services, troubleshoot issues, enforce these Terms, maintain security, comply with law, and
        improve product reliability. Whitestone does not claim ownership of your original user materials solely because you submit them to
        a service.
      </p>
      <h2>Education, Media, And Editorial Content</h2>
      <p>
        AI education pages, tutorials, AI news posts, API commentary, and product updates are provided for general
        informational purposes. Publication dates, product plans, third-party tools, laws, APIs, pricing, and technical behavior may
        change. You are responsible for verifying information before using it in business, legal, security, financial, or production
        decisions.
      </p>
      <h2>Fees, Subscriptions, Refunds, And Taxes</h2>
      <p>
        Paid services may be billed through third-party processors such as Stripe. Prices, billing intervals, plan features, usage limits,
        trials, renewals, cancellations, and refund eligibility may vary by product. You are responsible for applicable taxes unless
        otherwise stated. Unless a product-specific policy says otherwise, fees are non-refundable except where required by law or expressly
        approved by Whitestone.
      </p>
      <p>
        Subscription services may renew automatically unless canceled according to the applicable product flow. Failure to pay, chargeback
        abuse, payment processor risk flags, excessive usage, or violation of these Terms may result in downgrade, suspension, termination,
        or loss of access to paid features.
      </p>
      <h2>Third-Party Services</h2>
      <p>
        Whitestone may integrate with third-party services such as Google OAuth, Stripe, email providers, analytics providers, hosting
        providers, communications providers, and other software tools. Third-party services are governed by their own terms and policies,
        and Whitestone is not responsible for third-party acts, omissions, outages, or data practices.
      </p>
      <h2>Privacy And Data Protection</h2>
      <p>
        Whitestone's Privacy Policy and the data protection sections in these Terms explain how information may be collected, used,
        shared, retained, and protected across company services. By using the services, you acknowledge that information may be processed
        according to those notices and any applicable product-specific privacy terms.
      </p>
      <p>
        Whitestone aims to separate public frontend code from private credentials, protect admin routes with authenticated server-side
        sessions, validate form and API input server-side, rate-limit public forms and login attempts, maintain audit logs for sensitive
        admin activity, and use environment-based secret management for providers such as payment processors, OAuth providers, email
        services, analytics systems, and product APIs.
      </p>
      <h2>Intellectual Property</h2>
      <p>
        Whitestone Computing LLC, Whitestone Computing, and associated product names, logos, software names, interfaces, documentation,
        designs, workflows, code, content, and brand marks are owned by Whitestone Computing LLC or its licensors unless otherwise stated.
        These Terms do not grant ownership rights. You may not copy, resell, sublicense, impersonate, or misrepresent Whitestone assets.
      </p>
      <p>
        Feedback, suggestions, feature requests, bug reports, ideas, or improvement proposals may be used by Whitestone without restriction
        or compensation unless a separate written agreement says otherwise. You should not submit confidential ideas unless a written
        agreement is in place.
      </p>
      <h2>Service Changes, Beta Features, And Availability</h2>
      <p>
        Services may change, be limited, be discontinued, or contain beta features. Beta and experimental services may be unstable,
        incomplete, or changed without notice. Whitestone does not guarantee uninterrupted availability, error-free operation, permanent
        storage, or compatibility with all systems.
      </p>
      <p>
        Whitestone may modify, rename, consolidate, migrate, or discontinue products, sub-sites, APIs, documentation, article categories,
        pricing plans, admin features, and integrations as the company portfolio changes.
      </p>
      <h2>Security And Responsible Disclosure</h2>
      <p>
        You may not test, scan, attack, or probe Whitestone systems without written authorization. Security concerns, suspected
        vulnerabilities, abuse reports, or privacy issues should be sent to {supportEmail} with safe reproduction details. Do not access,
        modify, destroy, download, disclose, or exfiltrate data that does not belong to you.
      </p>
      <h2>Disclaimers And Limitation Of Liability</h2>
      <p>
        Services are provided "as is" and "as available" to the fullest extent permitted by law. Whitestone disclaims implied warranties,
        including merchantability, fitness for a particular purpose, and non-infringement. To the fullest extent permitted by law,
        Whitestone is not liable for indirect, incidental, special, consequential, punitive, lost-profit, lost-data, business interruption,
        or similar damages. Whitestone's total liability for claims relating to the services is limited to the amount you paid for the
        relevant service in the three months before the claim, or 100 dollars if no amount was paid, unless applicable law requires more.
      </p>
      <p>
        Whitestone does not warrant that outputs, articles, generated documents, API responses, product recommendations, automation
        actions, or educational materials will be accurate, complete, lawful for your use case, secure for every environment, or suitable
        for any particular business outcome.
      </p>
      <h2>Indemnity</h2>
      <p>
        You agree to defend, indemnify, and hold Whitestone Computing LLC harmless from claims, damages, liabilities, losses, and expenses
        arising from your content, your use of the services, your violation of these Terms, your violation of law, or your infringement of
        third-party rights.
      </p>
      <h2>Suspension And Termination</h2>
      <p>
        Whitestone may suspend or terminate access for misuse, risk, nonpayment, fraud, security issues, legal compliance, account
        inactivity, product discontinuation, or violation of these Terms. You may stop using the services at any time. Provisions that
        should reasonably survive termination will survive, including payment obligations, intellectual property, disclaimers, liability
        limits, indemnity, and dispute-related provisions.
      </p>
      <h2>Changes To These Terms</h2>
      <p>
        Whitestone may update these Terms as products, laws, providers, business operations, or platform requirements change. Updated
        terms will be posted with a revised date. Continued use after an update means you accept the updated Terms, except where law
        requires additional notice or consent.
      </p>
      <h2>Governing Law, Disputes, And Contact</h2>
      <p>
        These Terms are governed by applicable United States law and applicable state law, except where mandatory consumer protection law
        requires otherwise. Before filing a claim, you agree to contact Whitestone at {supportEmail} and attempt to resolve the dispute
        informally. Questions about these Terms may be sent to {supportEmail}.
      </p>
    </LegalPage>
  );
}

function Unsubscribe() {
  const location = useLocation();
  const [state, setState] = useState({ status: 'idle', message: '' });
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = query.get('token') || '';
  const initialEmail = query.get('email') || '';
  const [email, setEmail] = useState(initialEmail);

  async function unsubscribe(event) {
    event.preventDefault();
    setState({ status: 'loading', message: '' });
    try {
      await submitForm('/api/newsletter/unsubscribe', token ? { token } : { email });
      setState({ status: 'success', message: 'You have been unsubscribed from Whitestone marketing updates.' });
      window.localStorage.setItem('wc_newsletter_preference', JSON.stringify({ value: 'dismissed', savedAt: Date.now() }));
    } catch (error) {
      setState({ status: 'error', message: error.message || 'We could not update that subscription.' });
    }
  }

  return (
    <PageShell eyebrow="Email preferences" title="Unsubscribe from Whitestone updates.">
      <section className="form-panel legal-copy">
        <p>
          Use this page to stop newsletter, developer-update, AI education, software release, and AI news emails.
          Transactional account, security, billing, or support messages may still be sent when needed for a product you use.
        </p>
        <form className="newsletter-form inline" onSubmit={unsubscribe}>
          {!token && (
            <label>
              <span>Email address</span>
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email address" required />
            </label>
          )}
          <button className="button primary" type="submit" disabled={state.status === 'loading'}>
            {state.status === 'loading' ? 'Updating...' : 'Unsubscribe'}
          </button>
        </form>
        {state.message && <p className={`form-message ${state.status === 'error' ? 'error' : 'success'}`}>{state.message}</p>}
      </section>
    </PageShell>
  );
}

function LegalPage({ title, children }) {
  return (
    <PageShell eyebrow="Legal" title={title}>
      <article className="legal-copy">{children}</article>
    </PageShell>
  );
}

function LegalNoticeLinks({ scope, basePath }) {
  return (
    <aside className="legal-notice-strip">
      <div>
        <span className="eyebrow">{scope} notices</span>
        <p>These pages use the company-wide Whitestone Computing LLC Privacy Policy and Terms of Service.</p>
      </div>
      <div>
        <Link className="button small secondary" to={`${basePath}/privacy`}>Privacy Policy</Link>
        <Link className="button small secondary" to={`${basePath}/terms`}>Terms of Service</Link>
      </div>
    </aside>
  );
}

function PageShell({ eyebrow, title, children }) {
  return (
    <section className="page-shell">
      <div className="container page-intro">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
      </div>
      <div className="container page-content">{children}</div>
    </section>
  );
}

function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [state, setState] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    api('/api/admin/csrf').catch(() => {});
  }, []);

  async function login(event) {
    event.preventDefault();
    setState({ status: 'loading', message: '' });
    try {
      await api('/api/admin/login', {
        method: 'POST',
        headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
        body: JSON.stringify(form)
      });
      navigate('/admin');
    } catch (error) {
      setState({ status: 'error', message: error.message });
    }
  }

  return (
    <main className="admin-login">
      <Link className="brand login-brand" to="/">
        <BrandMark />
        <span><strong>Whitestone Computing</strong></span>
      </Link>
      <form className="login-panel" onSubmit={login} autoComplete="off">
        <Lock size={26} />
        <label>
          <span>Username</span>
          <input
            type="text"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            autoComplete="off"
            name="wc-admin-username"
            required
          />
        </label>
        <label>
          <span>Password</span>
          <input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} autoComplete="current-password" required />
        </label>
        <button className="button primary" type="submit" disabled={state.status === 'loading'}>
          {state.status === 'loading' ? 'Signing in...' : 'Sign In'}
        </button>
        {state.message && <p className="form-message error">{state.message}</p>}
      </form>
    </main>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [ops, setOps] = useState({ notifications: [], readiness: [], automation: null, moduleRegistry: null });
  const [paystubAnalytics, setPaystubAnalytics] = useState(null);
  const [filters, setFilters] = useState({ product: 'all', category: 'all', status: 'all', tier: 'all', dateRange: '30d' });
  const [error, setError] = useState('');

  async function loadAdminData() {
    try {
      const [overview, productData, analytics, tables, integrationData, notifications, readiness, automation, moduleRegistry, audience, aiContent] = await Promise.all([
        api('/api/admin/overview'),
        api('/api/admin/products'),
        api('/api/admin/analytics'),
        api('/api/admin/tables'),
        api('/api/admin/integrations'),
        api('/api/admin/notifications'),
        api('/api/admin/launch-readiness'),
        api('/api/admin/automation-summary'),
        api('/api/admin/software-modules'),
        api('/api/admin/newsletter/subscribers'),
        api('/api/admin/ai-content/posts')
      ]);
      setData({ overview, products: productData.products, analytics, tables, audience, aiContent });
      setIntegrations(integrationData);
      setOps({ notifications: notifications.notifications, readiness: readiness.readiness, automation, moduleRegistry });
      loadPaystubAnalytics(filters.dateRange);
      setError('');
    } catch (requestError) {
      setError(requestError.message);
      if (requestError.message.includes('authentication')) navigate('/admin/login');
    }
  }

  useEffect(() => {
    loadAdminData();
  }, [navigate]);

  useEffect(() => {
    if (data) loadPaystubAnalytics(filters.dateRange);
  }, [filters.dateRange]);

  async function loadPaystubAnalytics(range = filters.dateRange) {
    try {
      const result = await api(`/api/admin/paystub-instant/analytics?range=${encodeURIComponent(range)}`);
      setPaystubAnalytics(result);
    } catch (requestError) {
      setPaystubAnalytics({ connected: false, error: requestError.message });
    }
  }

  async function logout() {
    try {
      await api('/api/admin/logout', {
        method: 'POST',
        headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' }
      });
    } finally {
      navigate('/admin/login');
    }
  }

  const visibleProducts = useMemo(() => {
    if (!data) return [];
    return data.products.filter((product) => {
      const matchesProduct = filters.product === 'all' || product.id === filters.product;
      const matchesCategory = filters.category === 'all' || product.category === filters.category;
      const matchesStatus = filters.status === 'all' || product.productStatus === filters.status;
      return matchesProduct && matchesCategory && matchesStatus;
    });
  }, [data, filters.product, filters.category, filters.status]);

  if (error) {
    return <AdminFrame logout={logout}><div className="admin-empty">{error}</div></AdminFrame>;
  }

  if (!data) {
    return <AdminFrame logout={logout}><div className="admin-empty">Loading Whitestone admin dashboard...</div></AdminFrame>;
  }

  const cards = [
    ['Total revenue', currency(data.overview.totalRevenue), BarChart3],
    ['Monthly recurring revenue', currency(data.overview.monthlyRecurringRevenue), WalletCards],
    ['Total users', number(data.overview.totalUsers), Users],
    ['Active users', number(data.overview.activeUsers), Activity],
    ['New users today', number(data.overview.newUsersToday), Users],
    ['New users this month', number(data.overview.newUsersThisMonth), Users],
    ['Contact submissions', number(data.overview.totalContactSubmissions), Mail],
    ['Access requests', number(data.overview.totalInviteRequests), Mail],
    ['Support tickets', number(data.overview.totalSupportTickets), Ticket],
    ['Total payments', number(data.overview.totalPayments), WalletCards],
    ['Failed payments', number(data.overview.failedPayments), ShieldCheck],
    ['Conversion rate', `${data.overview.conversionRate}%`, Rocket],
    ['Product growth rate', `${data.overview.productGrowthRate}%`, Activity],
    ['Backend connectors', number(data.overview.registeredBackendConnectors), Network],
    ['Configured APIs', number(data.overview.configuredProductApis), PlugZap],
    ['Tracked product events', number(data.overview.totalProductAnalyticsEvents), Code2],
    ['Subscribed audience', number(data.audience.summary.subscribed), Mail]
  ];

  return (
    <AdminFrame logout={logout}>
      <section className="admin-hero">
        <div>
          <span className="eyebrow">Central admin</span>
          <h1>Whitestone Computing LLC operations dashboard</h1>
          <p>Shared analytics, forms, product health, revenue reporting, support intake, and audit visibility.</p>
        </div>
        <div className="admin-health">
          <ShieldCheck size={22} />
          <span>Protected server-side session</span>
        </div>
      </section>

      <AdminFilterSuite filters={filters} setFilters={setFilters} products={data.products} />

      <section className="overview-grid">
        {cards.map(([label, value, Icon]) => (
          <article className="admin-card" key={label}>
            <Icon size={19} />
            <span>{label}</span>
            <strong>{value}</strong>
          </article>
        ))}
      </section>

      <AdminIntelligence data={data} integrations={integrations} />
      <AdminMonitoring data={data} integrations={integrations} notifications={ops.notifications} />
      <PaystubInstantCommandCenter analytics={paystubAnalytics} range={filters.dateRange} refresh={() => loadPaystubAnalytics(filters.dateRange)} />
      <AdminOpsTools data={data} readiness={ops.readiness} refresh={loadAdminData} />
      <AdminAutomationCenter automation={ops.automation} products={data.products} refresh={loadAdminData} />
      <AdminModuleRegistry registry={ops.moduleRegistry} />
      <AdminEmailCenter audience={data.audience} refresh={loadAdminData} />
      <AdminContentStudio posts={data.aiContent?.posts || []} refresh={loadAdminData} />

      <section className="admin-section" id="products">
        <div className="admin-section-heading">
          <h2>Product breakdown</h2>
          <p>Users, subscriptions, revenue, support demand, conversion, and activity by SaaS brand.</p>
        </div>
        <div className="product-admin-grid">
          {visibleProducts.map((product) => <ProductAdminCard product={product} key={product.id} />)}
        </div>
      </section>

      <section className="admin-section charts-grid" id="analytics">
        <ChartPanel title="Revenue by product" data={data.analytics.charts.revenueByProduct} money />
        <ChartPanel title="Users by product" data={data.analytics.charts.usersByProduct} />
        <ChartPanel title="Signups over time" data={data.analytics.charts.signupsOverTime} />
        <ChartPanel title="Product usage over time" data={data.analytics.charts.productUsageOverTime} />
        <ChartPanel title="Support tickets by product" data={data.analytics.charts.supportTicketsByProduct} />
        <ChartPanel title="Access requests by product" data={data.analytics.charts.inviteRequestsByProduct} />
        <ChartPanel title="Conversion funnel" data={data.analytics.charts.conversionFunnel} />
        <ChartPanel title="Monthly growth trend" data={data.analytics.charts.monthlyGrowthTrend} suffix="%" />
        <ChartPanel title="Payment success vs failure" data={data.analytics.charts.paymentSuccessVsFailure} />
      </section>

      <section className="admin-section" id="revenue">
        <div className="admin-section-heading">
          <h2>Global subscription tiers</h2>
          <p>Whitestone recognizes only Free / Starter, Pro, and Business across the portfolio.</p>
        </div>
        <div className="tier-grid admin-tiers">
          {data.analytics.tiers.map((tier) => (
            <div className="tier" key={tier.tier}>
              <h3>{tier.tier}</h3>
              <p>{number(tier.users)} users</p>
              <strong>{currency(tier.revenue)}</strong>
              <span>{number(tier.subscriptions)} subscriptions</span>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-section table-grid" id="users">
        <DataTable title="Recent users" rows={data.tables.recentUsers} columns={['name', 'email', 'product', 'tier', 'status']} />
        <DataTable title="Recent payments" rows={data.tables.recentPayments} columns={['customer', 'product', 'tier', 'amount', 'status']} moneyColumn="amount" />
        <DataTable title="Recent contact submissions" rows={data.tables.recentContactSubmissions} columns={['name', 'email', 'company', 'productInterest', 'requestType', 'status']} empty="No contact submissions yet." />
        <DataTable title="Recent access requests" rows={data.tables.recentInviteRequests} columns={['fullName', 'email', 'productInterest', 'status']} empty="No access requests yet." />
        <DataTable title="Recent product activity" rows={data.tables.recentProductActivity} columns={['product', 'event', 'count']} />
        <DataTable title="Failed login attempts" rows={data.tables.failedLoginAttempts} columns={['actor', 'ip', 'createdAt']} empty="No failed login attempts." />
        <DataTable title="Admin audit logs" rows={data.tables.adminAuditLogs} columns={['actor', 'action', 'ip', 'createdAt']} empty="No audit logs yet." />
      </section>
    </AdminFrame>
  );
}

function AdminFilterSuite({ filters, setFilters, products }) {
  const categories = ['all', ...Array.from(new Set(products.map((product) => product.category)))];

  return (
    <section className="filter-suite" id="overview">
      <div className="filter-group">
        <h3>Portfolio</h3>
        <label>
          <span>Product</span>
          <select value={filters.product} onChange={(event) => setFilters({ ...filters, product: event.target.value })}>
            <option value="all">All</option>
            {products.map((product) => <option value={product.id} key={product.id}>{product.name}</option>)}
          </select>
        </label>
        <label>
          <span>Category</span>
          <select value={filters.category} onChange={(event) => setFilters({ ...filters, category: event.target.value })}>
            {categories.map((option) => <option value={option} key={option}>{option === 'all' ? 'All' : option}</option>)}
          </select>
        </label>
      </div>
      <div className="filter-group">
        <h3>Time And Status</h3>
        <label>
          <span>Date range</span>
          <select value={filters.dateRange} onChange={(event) => setFilters({ ...filters, dateRange: event.target.value })}>
            {['7d', '30d', '90d', '12m'].map((option) => <option key={option}>{option}</option>)}
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
            {['all', 'Active', 'Beta', 'Internal', 'Coming Soon'].map((option) => <option value={option} key={option}>{option === 'all' ? 'All' : option}</option>)}
          </select>
        </label>
      </div>
      <div className="filter-group">
        <h3>Commercial Segment</h3>
        <label>
          <span>Tier</span>
          <select value={filters.tier} onChange={(event) => setFilters({ ...filters, tier: event.target.value })}>
            {['all', 'Free / Starter', 'Pro', 'Business'].map((option) => <option value={option} key={option}>{option === 'all' ? 'All' : option}</option>)}
          </select>
        </label>
        <div className="filter-note">
          <ChevronDown size={16} />
          Filters apply to visible product cards and keep admin-only products internal.
        </div>
      </div>
    </section>
  );
}

function AdminIntelligence({ data, integrations }) {
  const topProduct = [...data.products].sort((a, b) => b.revenue - a.revenue)[0];
  const lowestProduct = [...data.products].sort((a, b) => a.activeUsers - b.activeUsers)[0];
  const integrationRows = integrations
    ? [
        ['Stripe billing', integrations.integrations.stripe],
        ['Google OAuth', integrations.integrations.googleOAuth],
        ['Email delivery', integrations.integrations.email],
        ['Admin analytics', integrations.integrations.analytics]
      ]
    : [];

  return (
    <section className="admin-intelligence">
      <article className="ops-card">
        <span className="eyebrow">Top performer</span>
        <h2>{topProduct?.name}</h2>
        <p>{currency(topProduct?.revenue || 0)} total revenue with {number(topProduct?.activeUsers || 0)} active users.</p>
      </article>
      <article className="ops-card">
        <span className="eyebrow">Needs attention</span>
        <h2>{lowestProduct?.name}</h2>
        <p>Lowest active-user base. Review activation, onboarding, pricing, and support tickets.</p>
      </article>
      <article className="ops-card integration-card">
        <span className="eyebrow">Integration readiness</span>
        <div className="integration-list">
          {integrationRows.map(([label, ready]) => (
            <span key={label} className={ready ? 'ready' : 'not-ready'}>
              <i />
              {label}
              <strong>{ready ? 'Ready' : 'Needs env'}</strong>
            </span>
          ))}
        </div>
      </article>
    </section>
  );
}

function AdminMonitoring({ data, integrations, notifications: serverNotifications = [] }) {
  const fallbackNotifications = [
    {
      level: data.overview.failedPayments > 20 ? 'High' : 'Normal',
      title: 'Payment Failure Watch',
      body: `${number(data.overview.failedPayments)} failed payments are currently visible across the portfolio.`
    },
    {
      level: data.overview.totalSupportTickets > 0 ? 'Review' : 'Clear',
      title: 'Unified Support Queue',
      body: `${number(data.overview.totalSupportTickets)} Whitestone support tickets are waiting in central intake.`
    },
    {
      level: integrations?.integrations.email ? 'Ready' : 'Needs Env',
      title: 'Unified Email Delivery',
      body: integrations?.integrations.email
        ? `${supportEmail} is configured as the central support sender.`
        : `Set EMAIL_API_KEY or SMTP credentials so ${supportEmail} can send confirmations.`
    },
    {
      level: integrations?.integrations.stripe ? 'Ready' : 'Needs Env',
      title: 'Billing Integration',
      body: integrations?.integrations.stripe
        ? 'Stripe server keys are present for central billing telemetry.'
        : 'Stripe production keys and webhooks still need to be configured before live payments.'
    }
  ];
  const notifications = serverNotifications.length
    ? serverNotifications.map((item) => ({ ...item, body: item.message }))
    : fallbackNotifications;

  return (
    <section className="admin-section" id="monitoring">
      <div className="admin-section-heading">
        <div>
          <h2>Monitoring And Notifications</h2>
          <p>Central alerts for revenue, support, integrations, delivery, and launch readiness.</p>
        </div>
      </div>
      <div className="notification-grid">
        {notifications.map((item) => (
          <article className={`notification-card ${item.level.toLowerCase().replaceAll(' ', '-')}`} key={item.title}>
            <Bell size={19} />
            <div>
              <span>{item.level}</span>
              <h3>{item.title}</h3>
              <p>{item.body || item.message}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function PaystubInstantCommandCenter({ analytics, range, refresh }) {
  if (!analytics) {
    return (
      <section className="admin-section" id="paystub-instant">
        <div className="admin-section-heading">
          <div>
            <h2>PayStub Instant Intelligence</h2>
            <p>Loading product-specific usage, checkout, template, tax form, and tool analytics.</p>
          </div>
        </div>
        <div className="admin-empty">Loading PayStub Instant analytics...</div>
      </section>
    );
  }

  if (!analytics.connected) {
    return (
      <section className="admin-section" id="paystub-instant">
        <div className="admin-section-heading">
          <div>
            <h2>PayStub Instant Intelligence</h2>
            <p>Connect the PayStub Instant backend to see live user behavior, purchases, templates, and checkout recovery.</p>
          </div>
          <button className="button secondary small" type="button" onClick={refresh}>Retry</button>
        </div>
        <div className="admin-empty">{analytics.error || analytics.setup || 'PayStub Instant connector is not available.'}</div>
      </section>
    );
  }

  const psi = analytics.paystubInstant || {};
  const summary = psi.summary || {};
  const templateRows = psi.templates?.paystubTemplates || analytics.usage?.topTemplates || [];
  const taxTemplateRows = [
    ...(psi.taxForms?.templateInterest || []),
    ...(psi.taxForms?.savedDrafts || []).map((row) => ({ ...row, events: row.savedDrafts, source: 'saved draft' }))
  ].slice(0, 30);
  const commerceRows = psi.commerce?.purchasesByProduct || [];
  const abandonedRows = analytics.abandonedCheckouts || [];
  const pageRows = psi.rankings?.mostViewedPages || analytics.traffic?.topPages || [];
  const leastViewedRows = psi.rankings?.leastViewedPages || [];
  const userRows = psi.users?.recentAccounts || analytics.users || [];
  const activityRows = psi.activity || [];
  const overview = {
    ...(analytics.overview || {}),
    monthlyRevenue: analytics.overview?.monthlyRevenue ?? summary.revenue ?? 0,
    paidExports: analytics.overview?.paidExports ?? summary.paidCheckouts ?? 0,
    abandonedCheckouts: analytics.overview?.abandonedCheckouts ?? summary.abandonedCheckouts ?? abandonedRows.length,
    conversionRate: analytics.overview?.conversionRate ?? summary.checkoutConversionRate ?? 0
  };

  return (
    <section className="admin-section paystub-intel-section" id="paystub-instant">
      <div className="admin-section-heading paystub-admin-heading">
        <div>
          <span className="eyebrow">Admin analytics</span>
          <h2>PayStub Instant Dashboard</h2>
          <p>Conversion funnel, SEO traffic, checkout behavior, templates, calculators, ad performance, and revenue trends.</p>
        </div>
        <div className="quick-actions">
          <a className="button secondary small" href="https://paystubinstant.com/admin" target="_blank" rel="noreferrer">Open Standalone Admin</a>
          <button className="button secondary small" type="button" onClick={refresh}>Refresh Dashboard</button>
        </div>
      </div>

      <PaystubMasterMetricGrid overview={overview} />
      <PaystubMasterNotificationBar notifications={analytics.notifications || []} />
      <PaystubMasterFunnel funnel={analytics.funnel || {}} />

      <div className="admin-section charts-grid">
        <PaystubFunnelPanel title="Payroll funnel" rows={psi.funnels?.payroll || []} />
        <PaystubFunnelPanel title="Tax form funnel" rows={psi.funnels?.taxForms || []} />
        <PaystubFunnelPanel title="Document tools funnel" rows={psi.funnels?.tools || []} />
        <ChartPanel title="Purchases by product" data={commerceRows.map((row) => ({ label: labelize(row.product_type), value: Number(row.purchases || 0) }))} />
        <ChartPanel title="Revenue by product" data={commerceRows.map((row) => ({ label: labelize(row.product_type), value: Number(row.revenue || 0) }))} money />
        <ChartPanel title="Traffic sources" data={(psi.rankings?.trafficSources || []).map((row) => ({ label: row.source, value: Number(row.views || 0) }))} />
      </div>

      <AbandonedPaystubRecovery rows={abandonedRows} refresh={refresh} />
      <PaystubInstantOpsDesk analytics={analytics} refresh={refresh} />

      <section className="admin-section table-grid">
        <SmartAdminTable title="Most used paystub templates" rows={templateRows} columns={['template', 'created', 'previews', 'paidRecords', 'paid', 'revenue', 'paidRate', 'conversionRate', 'lastCreated']} />
        <SmartAdminTable title="Most purchased products" rows={commerceRows} columns={['product_type', 'purchases', 'revenue', 'lastPurchased']} moneyColumns={['revenue']} />
        <SmartAdminTable title="Tax form demand" rows={taxTemplateRows} columns={['template', 'events', 'sessions', 'savedDrafts', 'users', 'source', 'lastEvent', 'lastUpdated']} />
        <SmartAdminTable title="Most viewed pages" rows={pageRows} columns={['page_path', 'views', 'sessions']} />
        <SmartAdminTable title="Least viewed pages" rows={leastViewedRows} columns={['page_path', 'views', 'sessions']} />
        <SmartAdminTable title="User records and value" rows={userRows} columns={['email', 'name', 'auth_provider', 'payrollRecords', 'payrollDrafts', 'taxDrafts', 'payments', 'revenue', 'last_login_at', 'created_at']} moneyColumns={['revenue']} />
        <SmartAdminTable title="Recent tax drafts" rows={psi.taxForms?.recentDrafts || []} columns={['title', 'template_id', 'taxpayer_name', 'tax_year', 'email', 'updated_at']} />
        <SmartAdminTable title="Recent payroll drafts" rows={psi.drafts?.recentPayrollDrafts || []} columns={['title', 'template_type', 'employee_name', 'company_name', 'email', 'updated_at']} />
        <SmartAdminTable title="Document tool usage" rows={psi.tools?.toolUsage || []} columns={['tool_type', 'event_type', 'events', 'sessions']} />
        <SmartAdminTable title="Recent PSI activity" rows={activityRows} columns={['event_name', 'page_path', 'session_id', 'metadata', 'created_at']} />
        <SmartAdminTable title="Recent payments" rows={analytics.payments || []} columns={['customer_email', 'customer_name', 'product_type', 'template_id', 'amount', 'status', 'stripe_session_id', 'created_at']} moneyColumns={['amount']} />
        <SmartAdminTable title="Payroll record master log" rows={analytics.payrollRecordLog || []} columns={['customer_email', 'customer_name', 'account_or_guest', 'employee_name', 'company_name', 'template_type', 'payment_status', 'amount', 'created_at']} moneyColumns={['amount']} />
        <SmartAdminTable title="Recent exports" rows={analytics.recentExports || []} columns={['id', 'paystub_id', 'employee_name', 'company_name', 'template_id', 'export_type', 'is_watermarked', 'created_at']} />
        <SmartAdminTable title="Failed payments" rows={analytics.failedPayments || []} columns={['id', 'product_type', 'template_id', 'amount', 'status', 'failure_reason', 'created_at']} moneyColumns={['amount']} />
        <SmartAdminTable title="Subscriptions" rows={analytics.subscriptions || []} columns={['plan', 'status', 'current_period_end', 'cancel_at_period_end', 'trial_end', 'created_at']} />
        <SmartAdminTable title="Stripe webhook events" rows={analytics.stripeEvents || []} columns={['id', 'event_type', 'status', 'attempts', 'error_message', 'processed_at']} />
        <SmartAdminTable title="Email delivery" rows={analytics.emailDeliveries || []} columns={['recipient_email', 'email_type', 'status', 'paystub_id', 'created_at']} />
        <SmartAdminTable title="Support tickets" rows={analytics.supportTickets || []} columns={['id', 'category', 'priority', 'status', 'assigned_to', 'email', 'topic', 'related_payment_id', 'created_at']} />
        <SmartAdminTable title="Support requests" rows={analytics.support || []} columns={['id', 'email', 'request_type', 'topic', 'paystub_id', 'checkout_session_id', 'status', 'created_at']} />
        <SmartAdminTable title="Admin notifications" rows={analytics.notifications || []} columns={['category', 'title', 'severity', 'status', 'paystub_id', 'checkout_session_id', 'created_at']} />
        <SmartAdminTable title="Abuse reports" rows={analytics.abuseReports || []} columns={['id', 'session_id', 'user_id', 'flag_type', 'severity', 'status', 'created_at']} />
        <SmartAdminTable title="High usage users" rows={analytics.highUsageUsers || []} columns={['user_id', 'email', 'event_count', 'paystub_count', 'last_seen']} />
        <SmartAdminTable title="Suspicious accounts" rows={analytics.suspiciousAccounts || []} columns={['user_id', 'flags', 'highestSeverity', 'lastFlagged']} />
        <SmartAdminTable title="Monitoring alerts" rows={analytics.system?.monitoringAlerts || analytics.system?.monitoring?.alerts || []} columns={['category', 'status', 'message', 'count', 'threshold']} />
        <SmartAdminTable title="Recent error logs" rows={analytics.recentErrorLogs || []} columns={['level', 'category', 'message', 'created_at']} />
      </section>
    </section>
  );
}

function PaystubFunnelPanel({ title, rows = [] }) {
  const data = rows.length ? rows : [{ label: 'No data yet', value: 0 }];
  return <ChartPanel title={title} data={data.map((item) => ({ label: item.label, value: Number(item.value || 0) }))} />;
}

function PaystubMasterMetricGrid({ overview = {} }) {
  const cards = [
    ['revenueToday', 'Revenue today'],
    ['monthlyRevenue', 'Monthly revenue'],
    ['totalExports', 'Total exports'],
    ['paidExports', 'Paid exports'],
    ['conversionRate', 'Conversion rate'],
    ['checkoutAbandonment', 'Checkout abandonment'],
    ['abandonedCheckouts', 'Abandoned checkouts'],
    ['remindableAbandonedCheckouts', 'Remindable abandoned checkouts']
  ];

  return (
    <div className="overview-grid compact paystub-admin-metrics">
      {cards.map(([key, label]) => (
        <article className="admin-card" key={key}>
          <WalletCards size={18} />
          <span>{label}</span>
          <strong>{formatPaystubDashboardValue(key, overview[key])}</strong>
        </article>
      ))}
    </div>
  );
}

function PaystubMasterNotificationBar({ notifications = [] }) {
  const unread = notifications.filter((item) => item.status === 'unread').slice(0, 4);
  if (!unread.length) {
    return (
      <section className="admin-section paystub-notification-panel">
        <div className="admin-section-heading">
          <div>
            <h2>Notification Center</h2>
            <p>Purchases, support requests, export delivery, and checkout references will appear here.</p>
          </div>
          <strong>No unread alerts</strong>
        </div>
      </section>
    );
  }

  return (
    <section className="admin-section paystub-notification-panel alert">
      <div className="admin-section-heading">
        <div>
          <h2>Notification Center</h2>
          <p>Unread purchase and support alerts from PayStub Instant.</p>
        </div>
        <strong>{unread.length} recent unread</strong>
      </div>
      <div className="notification-grid paystub-notification-grid">
        {unread.map((item) => (
          <article className="notification-card review" key={item.id || item.title}>
            <Bell size={19} />
            <div>
              <span>{labelize(item.category)} - {item.severity || 'Review'}</span>
              <h3>{item.title}</h3>
              <p>{item.message}</p>
              <small>Payroll: {item.paystub_id || 'none'} - Checkout: {item.checkout_session_id || 'none'} - Ticket: {item.support_ticket_id || 'none'}</small>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function PaystubMasterFunnel({ funnel = {} }) {
  const steps = [
    ['Page view sessions', funnel.pageViewSessions],
    ['Payroll previews', funnel.payrollPreviews],
    ['Free previews', funnel.freePreviewExports],
    ['Checkout starts', funnel.checkoutStarts],
    ['Checkout completions', funnel.checkoutCompletions],
    ['Paid exports', funnel.paidExports]
  ];
  const max = Math.max(...steps.map(([, value]) => Number(value) || 0), 1);

  return (
    <section className="admin-section paystub-conversion-funnel">
      <div className="admin-section-heading">
        <div>
          <h2>Conversion Funnel</h2>
          <p>Tracks the path from organic entry to preview, checkout, and export.</p>
        </div>
        <strong>Checkout abandonment: {formatPaystubDashboardValue('checkoutAbandonment', funnel.abandonmentRate)}</strong>
      </div>
      <div className="paystub-funnel-rows">
        {steps.map(([label, value]) => {
          const width = `${Math.max(6, Math.round(((Number(value) || 0) / max) * 100))}%`;
          return (
            <div className="paystub-funnel-row" key={label}>
              <div><span>{label}</span><strong>{number(value || 0)}</strong></div>
              <i><b style={{ width }} /></i>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function formatPaystubDashboardValue(key, value) {
  if (/revenue/i.test(key)) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value) || 0);
  }
  if (/rate|abandonment/i.test(key)) return `${Number(value || 0)}%`;
  return number(value || 0);
}

function PaystubInstantOpsDesk({ analytics, refresh }) {
  const [lookupQuery, setLookupQuery] = useState('');
  const [supportLookup, setSupportLookup] = useState(null);
  const [opsLookup, setOpsLookup] = useState(null);
  const [status, setStatus] = useState('');
  const [exportForm, setExportForm] = useState({ email: '', paystubId: '', downloadToken: '' });
  const [regenForm, setRegenForm] = useState({ email: '', paystubId: '' });
  const [moderation, setModeration] = useState({ targetType: 'session', targetValue: '', reason: '' });

  async function adminPost(path, body) {
    const result = await api(path, {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify(body)
    });
    return result;
  }

  async function runSupportLookup(event) {
    event.preventDefault();
    setStatus('');
    try {
      const result = await api(`/api/admin/paystub-instant/support/lookup?q=${encodeURIComponent(lookupQuery)}`);
      setSupportLookup(result);
      setStatus('Support lookup complete.');
    } catch (requestError) {
      setStatus(requestError.message);
    }
  }

  async function runOpsLookup(event) {
    event.preventDefault();
    setStatus('');
    try {
      const result = await api(`/api/admin/paystub-instant/ops/lookup?q=${encodeURIComponent(lookupQuery)}`);
      setOpsLookup(result);
      setStatus('Operations lookup complete.');
    } catch (requestError) {
      setStatus(requestError.message);
    }
  }

  async function resendExport(event) {
    event.preventDefault();
    setStatus('');
    try {
      const result = await adminPost('/api/admin/paystub-instant/support/resend-export', exportForm);
      setStatus(result.message || 'Secure export link resent.');
      refresh();
    } catch (requestError) {
      setStatus(requestError.message);
    }
  }

  async function regeneratePdf(event) {
    event.preventDefault();
    setStatus('');
    try {
      const result = await adminPost('/api/admin/paystub-instant/support/regenerate-pdf', regenForm);
      setStatus(result.message || 'Clean PDF regenerated.');
      refresh();
    } catch (requestError) {
      setStatus(requestError.message);
    }
  }

  async function recordModeration(event) {
    event.preventDefault();
    setStatus('');
    try {
      const result = await adminPost('/api/admin/paystub-instant/ops/moderation-note', moderation);
      setStatus(result.message || 'Moderation review note recorded.');
      setModeration({ targetType: 'session', targetValue: '', reason: '' });
      refresh();
    } catch (requestError) {
      setStatus(requestError.message);
    }
  }

  return (
    <section className="admin-section paystub-ops-desk">
      <div className="admin-section-heading">
        <div>
          <h2>PayStub Instant Operations Desk</h2>
          <p>All support, lookup, resend, PDF regeneration, moderation, payment, export, email, and monitoring tools from the standalone PayStub admin are available here.</p>
        </div>
        {status && <strong>{status}</strong>}
      </div>

      <div className="ops-workbench-grid paystub-ops-grid">
        <div className="ops-panel">
          <div className="panel-head">
            <h3>Customer and record lookup</h3>
            <span>Search by email, session, payment, export, token, record, or checkout ID</span>
          </div>
          <form className="automation-form" onSubmit={runSupportLookup}>
            <label>
              <span>Lookup query</span>
              <input value={lookupQuery} onChange={(event) => setLookupQuery(event.target.value)} placeholder="Email, payment, payroll ID, token..." />
            </label>
            <div className="quick-actions">
              <button className="button secondary" type="submit">Support Lookup</button>
              <button className="button secondary" type="button" onClick={runOpsLookup}>Operations Lookup</button>
            </div>
          </form>
        </div>

        <div className="ops-panel">
          <div className="panel-head">
            <h3>Recovery tools</h3>
            <span>Resend links and rebuild clean paid PDFs</span>
          </div>
          <form className="automation-form" onSubmit={resendExport}>
            <label><span>Customer email</span><input value={exportForm.email} onChange={(event) => setExportForm({ ...exportForm, email: event.target.value })} /></label>
            <label><span>Payroll record ID</span><input value={exportForm.paystubId} onChange={(event) => setExportForm({ ...exportForm, paystubId: event.target.value })} /></label>
            <label><span>Download token optional</span><input value={exportForm.downloadToken} onChange={(event) => setExportForm({ ...exportForm, downloadToken: event.target.value })} /></label>
            <button className="button secondary" type="submit">Resend Export Link</button>
          </form>
        </div>

        <div className="ops-panel">
          <div className="panel-head">
            <h3>Regenerate PDF</h3>
            <span>Use when a paid export needs to be rebuilt</span>
          </div>
          <form className="automation-form" onSubmit={regeneratePdf}>
            <label><span>Payroll record ID</span><input value={regenForm.paystubId} onChange={(event) => setRegenForm({ ...regenForm, paystubId: event.target.value })} /></label>
            <label><span>Email regenerated PDF optional</span><input value={regenForm.email} onChange={(event) => setRegenForm({ ...regenForm, email: event.target.value })} /></label>
            <button className="button primary" type="submit">Regenerate Clean PDF</button>
          </form>
        </div>

        <div className="ops-panel">
          <div className="panel-head">
            <h3>Moderation note</h3>
            <span>Record review context without changing customer access</span>
          </div>
          <form className="automation-form" onSubmit={recordModeration}>
            <label>
              <span>Target type</span>
              <select value={moderation.targetType} onChange={(event) => setModeration({ ...moderation, targetType: event.target.value })}>
                <option value="session">Session</option>
                <option value="user">User</option>
                <option value="email">Email</option>
                <option value="ip">IP</option>
                <option value="paystub">Payroll record</option>
              </select>
            </label>
            <label><span>Target value</span><input value={moderation.targetValue} onChange={(event) => setModeration({ ...moderation, targetValue: event.target.value })} /></label>
            <label><span>Reason</span><input value={moderation.reason} onChange={(event) => setModeration({ ...moderation, reason: event.target.value })} /></label>
            <button className="button secondary" type="submit">Record Review Note</button>
          </form>
        </div>
      </div>

      <section className="admin-section table-grid">
        <SmartAdminTable title="System monitoring" rows={analytics.system?.monitoring?.alerts || []} columns={['category', 'status', 'message', 'count', 'threshold']} />
        <SmartAdminTable title="Failed exports" rows={analytics.failedExports || []} columns={['created_at', 'session_id', 'page_path', 'metadata_json']} />
        <SmartAdminTable title="Refunds" rows={analytics.revenue?.refunds || []} columns={['product_type', 'amount', 'refunded_amount', 'status', 'created_at']} moneyColumns={['amount', 'refunded_amount']} />
        <SmartAdminTable title="Moderation actions" rows={analytics.moderationActions || []} columns={['admin_action', 'target_type', 'target_value', 'reason', 'status', 'created_at']} />
      </section>

      {supportLookup && (
        <section className="admin-section table-grid">
          <SmartAdminTable title="Lookup support requests" rows={supportLookup.supportRequests || []} columns={['id', 'email', 'request_type', 'topic', 'paystub_id', 'checkout_session_id', 'status', 'created_at']} />
          <SmartAdminTable title="Lookup support tickets" rows={supportLookup.supportTickets || []} columns={['id', 'email', 'category', 'priority', 'status', 'topic', 'related_payment_id', 'related_export_id', 'related_paystub_id', 'created_at']} />
          <SmartAdminTable title="Lookup payments" rows={supportLookup.payments || []} columns={['id', 'session_id', 'user_id', 'paystub_id', 'stripe_session_id', 'product_type', 'template_id', 'amount', 'status', 'created_at']} moneyColumns={['amount']} />
          <SmartAdminTable title="Lookup exports" rows={supportLookup.exports || []} columns={['id', 'paystub_id', 'session_id', 'export_type', 'template_id', 'is_watermarked', 'download_token', 'created_at']} />
        </section>
      )}

      {opsLookup && (
        <section className="admin-section table-grid">
          <SmartAdminTable title="Operations paystubs" rows={opsLookup.paystubs || []} columns={['id', 'session_id', 'user_id', 'company_name', 'employee_name', 'gross_pay', 'net_pay', 'template_type', 'is_paid', 'created_at']} moneyColumns={['gross_pay', 'net_pay']} />
          <SmartAdminTable title="Operations payments" rows={opsLookup.payments || []} columns={['id', 'customer_email', 'customer_name', 'product_type', 'template_id', 'amount', 'status', 'stripe_session_id', 'created_at']} moneyColumns={['amount']} />
          <SmartAdminTable title="Operations exports" rows={opsLookup.exports || []} columns={['id', 'paystub_id', 'employee_name', 'company_name', 'export_type', 'template_id', 'download_token', 'created_at']} />
          <SmartAdminTable title="Operations emails" rows={opsLookup.emails || []} columns={['recipient_email', 'email_type', 'status', 'paystub_id', 'created_at']} />
          <SmartAdminTable title="Operations users" rows={opsLookup.users || []} columns={['id', 'email', 'name', 'created_at']} />
          <SmartAdminTable title="Operations support" rows={opsLookup.supportRequests || []} columns={['id', 'email', 'request_type', 'topic', 'paystub_id', 'checkout_session_id', 'status', 'created_at']} />
        </section>
      )}
    </section>
  );
}

function AbandonedPaystubRecovery({ rows = [], refresh }) {
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState('');
  const visibleRows = rows.slice(0, 20);

  async function sendReminder(row) {
    setStatus('');
    setBusyId(row.id);
    try {
      const result = await api(`/api/admin/paystub-instant/checkouts/${encodeURIComponent(row.id)}/reminder`, {
        method: 'POST',
        headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
        body: JSON.stringify({ email: row.customer_email })
      });
      setStatus(result.message || 'Reminder sent.');
      refresh();
    } catch (requestError) {
      setStatus(requestError.message);
    } finally {
      setBusyId('');
    }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading">
        <div>
          <h2>Abandoned Checkout Recovery</h2>
          <p>Open PayStub Instant checkouts with customer emails, product details, reminders, and preview resume links.</p>
        </div>
        <strong>{number(rows.length)} open checkouts</strong>
      </div>
      {status && <div className="admin-empty compact-status">{status}</div>}
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              {['Customer', 'Record', 'Product', 'Started', 'Reminder', 'Action'].map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 ? (
              <tr><td colSpan="6">No abandoned checkouts for this window.</td></tr>
            ) : visibleRows.map((row) => (
              <tr key={row.id}>
                <td><strong>{row.customer_name || 'Guest'}</strong><small>{row.customer_email || 'No email captured'}</small></td>
                <td><strong>{row.employee_name || row.paystub_id || 'Payroll record'}</strong><small>{row.company_name || row.template_type || row.template_id || 'Template not captured'}</small></td>
                <td><strong>{row.product_label || labelize(row.product_type)}</strong><small>{currency(row.amount || 0)}</small></td>
                <td><strong>{formatMinutesAgo(row.minutes_since_started)}</strong><small>{formatCell(row.created_at, 'created_at')}</small></td>
                <td><strong>{number(row.reminder_count || 0)}</strong><small>{row.reminder_sent_at ? formatCell(row.reminder_sent_at, 'created_at') : 'Not sent'}</small></td>
                <td>
                  <button className="button small secondary" type="button" disabled={busyId === row.id || !row.can_send_reminder} onClick={() => sendReminder(row)}>
                    {busyId === row.id ? 'Sending' : 'Send Email'}
                  </button>
                  {row.resume_url && <a className="button small ghost" href={row.resume_url} target="_blank" rel="noreferrer">Open</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SmartAdminTable({ title, rows = [], columns = [], moneyColumns = [] }) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: columns[0] || '', direction: 'desc' });
  const filteredRows = useMemo(() => {
    const lowered = query.trim().toLowerCase();
    const filtered = lowered
      ? rows.filter((row) => JSON.stringify(row).toLowerCase().includes(lowered))
      : rows;
    return [...filtered].sort((a, b) => compareSmartValues(a[sort.key], b[sort.key], sort.direction));
  }, [rows, query, sort.key, sort.direction]);

  function changeSort(column) {
    setSort((current) => ({
      key: column,
      direction: current.key === column && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  }

  return (
    <article className="data-table smart-admin-table">
      <div className="smart-table-head">
        <h3>{title}</h3>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter rows" aria-label={`Filter ${title}`} />
      </div>
      {filteredRows.length === 0 ? (
        <p className="empty-state">No records.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>{columns.map((column) => <th key={column}><button type="button" onClick={() => changeSort(column)}>{labelize(column)}</button></th>)}</tr>
            </thead>
            <tbody>
              {filteredRows.map((row, index) => (
                <tr key={row.id || `${title}-${index}`}>
                  {columns.map((column) => (
                    <td key={column}>{moneyColumns.includes(column) ? currency(row[column] || 0) : formatSmartCell(row[column], column)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function compareSmartValues(a, b, direction) {
  const multiplier = direction === 'asc' ? 1 : -1;
  const numericA = Number(a);
  const numericB = Number(b);
  if (Number.isFinite(numericA) && Number.isFinite(numericB)) return (numericA - numericB) * multiplier;
  return String(a || '').localeCompare(String(b || '')) * multiplier;
}

function formatSmartCell(value, column) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value).slice(0, 180);
  if (/created|updated|login|purchased|started|event/i.test(column)) return formatCell(value, column);
  if (/rate/i.test(column)) return `${value}%`;
  return String(value);
}

function formatMinutesAgo(value) {
  const minutes = Number(value || 0);
  if (minutes < 60) return `${Math.max(minutes, 0)} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} hr ago`;
  return `${Math.round(minutes / 1440)} days ago`;
}

function AdminOpsTools({ data, readiness, refresh }) {
  const [updatingTicket, setUpdatingTicket] = useState('');
  const tickets = data.tables.recentSupportTickets || [];
  const exportTypes = [
    ['Support CSV', 'support'],
    ['Access Requests CSV', 'invites'],
    ['Contacts CSV', 'contacts'],
    ['Users CSV', 'users'],
    ['Payments CSV', 'payments'],
    ['Audit CSV', 'audit']
  ];

  async function updateTicketStatus(ticketId, status) {
    setUpdatingTicket(ticketId);
    try {
      await api(`/api/admin/support/${ticketId}/status`, {
        method: 'PATCH',
        headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
        body: JSON.stringify({ status })
      });
      await refresh();
    } finally {
      setUpdatingTicket('');
    }
  }

  return (
    <section className="admin-section ops-workbench" id="support">
      <div className="admin-section-heading">
        <div>
          <h2>Operations Workbench</h2>
          <p>Manage the shared Whitestone intake queue, export operating data, and review launch blockers by product.</p>
        </div>
        <button className="button secondary" type="button" onClick={refresh}>
          <Activity size={17} /> Refresh
        </button>
      </div>

      <div className="ops-workbench-grid">
        <article className="ops-panel support-panel">
          <div className="panel-head">
            <h3>Unified support queue</h3>
            <span>{number(tickets.length)} recent</span>
          </div>
          {tickets.length === 0 ? (
            <p className="empty-state">No support tickets yet.</p>
          ) : (
            <div className="ticket-list">
              {tickets.map((ticket) => (
                <div className="ticket-row" key={ticket.id}>
                  <div>
                    <strong>{ticket.name}</strong>
                    <span>{productOptionLabel(ticket.product)} / {ticket.category || 'General'} / {ticket.priority}</span>
                    <p>{ticket.issue || ticket.message}</p>
                  </div>
                  <select
                    value={ticket.status}
                    disabled={updatingTicket === ticket.id}
                    onChange={(event) => updateTicketStatus(ticket.id, event.target.value)}
                  >
                    {['New', 'Open', 'In Review', 'Waiting On User', 'Resolved', 'Closed'].map((status) => (
                      <option key={status}>{status}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </article>

        <article className="ops-panel">
          <div className="panel-head">
            <h3>Admin exports</h3>
            <span>CSV</span>
          </div>
          <div className="export-grid">
            {exportTypes.map(([label, type]) => (
              <a className="export-link" href={`/api/admin/export/${type}.csv`} key={type}>
                <FileText size={16} /> {label}
              </a>
            ))}
          </div>
        </article>
      </div>

      <div className="readiness-grid" id="audit-logs">
        {readiness.map((product) => (
          <article className="readiness-card" key={product.id}>
            <div className="panel-head">
              <h3>{product.name}</h3>
              <strong>{product.readinessScore}%</strong>
            </div>
            <div className="readiness-track"><i style={{ width: `${product.readinessScore}%` }} /></div>
            <ul>
              {product.checks.map((check) => (
                <li className={check.ready ? 'ready' : 'not-ready'} key={check.label}>
                  <CheckCircle2 size={15} /> {check.label}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdminAutomationCenter({ automation, products, refresh }) {
  const [emailDraft, setEmailDraft] = useState({
    product: 'smartdoctools',
    flow: 'support-ticket-opened',
    recipient: supportEmail
  });
  const [broadcast, setBroadcast] = useState({
    product: 'smartdoctools',
    subject: 'Whitestone product update',
    message: 'A product update is ready for review in the Whitestone central admin panel.'
  });
  const [message, setMessage] = useState('');
  const flows = automation?.activeFlows || [];

  async function runEmailTest(event) {
    event.preventDefault();
    const result = await api('/api/admin/email-automation/test', {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify(emailDraft)
    });
    setMessage(result.simulation ? 'Email automation simulated. Add EMAIL_API_KEY to send for real.' : 'Email automation queued.');
    await refresh();
  }

  async function createBroadcast(event) {
    event.preventDefault();
    const result = await api('/api/admin/broadcasts', {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify(broadcast)
    });
    setMessage(`Broadcast saved as ${result.record.status}.`);
    await refresh();
  }

  return (
    <section className="admin-section automation-center" id="invites">
      <div className="admin-section-heading">
        <div>
          <h2>Email Automation And Centralization</h2>
          <p>Stage confirmations, alerts, and product broadcasts from one Whitestone admin surface.</p>
        </div>
        <div className={automation?.emailProviderReady ? 'automation-pill ready' : 'automation-pill'}>
          <Mail size={16} /> {automation?.emailProviderReady ? 'Email live' : 'Simulation mode'}
        </div>
      </div>

      <div className="automation-grid">
        <article className="ops-panel">
          <div className="panel-head">
            <h3>Automation flows</h3>
            <span>{number(automation?.pendingEmailCount || 0)} pending signals</span>
          </div>
          <div className="flow-list">
            {flows.map((flow) => (
              <div className="flow-row" key={flow.id}>
                <div>
                  <strong>{flow.name}</strong>
                  <span>{flow.trigger}</span>
                </div>
                <em>{flow.status}</em>
              </div>
            ))}
          </div>
        </article>

        <form className="ops-panel automation-form" onSubmit={runEmailTest}>
          <div className="panel-head">
            <h3>Test email flow</h3>
            <span>{automation?.supportInbox || supportEmail}</span>
          </div>
          <label>
            <span>Product</span>
            <select value={emailDraft.product} onChange={(event) => setEmailDraft({ ...emailDraft, product: event.target.value })}>
              {products.map((product) => <option value={product.id} key={product.id}>{product.name}</option>)}
            </select>
          </label>
          <label>
            <span>Flow</span>
            <select value={emailDraft.flow} onChange={(event) => setEmailDraft({ ...emailDraft, flow: event.target.value })}>
              {flows.map((flow) => <option value={flow.id} key={flow.id}>{flow.name}</option>)}
            </select>
          </label>
          <label>
            <span>Recipient</span>
            <input value={emailDraft.recipient} onChange={(event) => setEmailDraft({ ...emailDraft, recipient: event.target.value })} />
          </label>
          <button className="button secondary" type="submit">Run Test</button>
        </form>

        <form className="ops-panel automation-form broadcast-form" onSubmit={createBroadcast}>
          <div className="panel-head">
            <h3>Product broadcast</h3>
            <span>Internal queue</span>
          </div>
          <label>
            <span>Product</span>
            <select value={broadcast.product} onChange={(event) => setBroadcast({ ...broadcast, product: event.target.value })}>
              {products.map((product) => <option value={product.id} key={product.id}>{product.name}</option>)}
            </select>
          </label>
          <label>
            <span>Subject</span>
            <input value={broadcast.subject} onChange={(event) => setBroadcast({ ...broadcast, subject: event.target.value })} />
          </label>
          <label className="wide">
            <span>Message</span>
            <textarea value={broadcast.message} onChange={(event) => setBroadcast({ ...broadcast, message: event.target.value })} />
          </label>
          <button className="button primary" type="submit">Save Broadcast</button>
        </form>
      </div>

      {message && <p className="form-message success">{message}</p>}
      <DataTable
        title="Recent automation activity"
        rows={automation?.recentAutomations || []}
        columns={['kind', 'product', 'flow', 'subject', 'status', 'createdAt']}
        empty="No automation activity yet."
      />
    </section>
  );
}

function AdminEmailCenter({ audience, refresh }) {
  const [filters, setFilters] = useState({ search: '', source: 'all', status: 'subscribed', type: 'all' });
  const [draft, setDraft] = useState({
    subject: 'Whitestone Computing update',
    message: 'A useful Whitestone Computing update is ready.\n\nUnsubscribe anytime using the unsubscribe link included in this email.',
    recipientMode: 'all_subscribed',
    testSendId: ''
  });
  const [message, setMessage] = useState('');
  const subscribers = audience?.subscribers || [];
  const sources = useMemo(() => ['all', ...Array.from(new Set(subscribers.flatMap((item) => item.sources || []))).filter(Boolean)], [subscribers]);
  const filtered = useMemo(() => subscribers.filter((item) => {
    const search = filters.search.trim().toLowerCase();
    const matchesSearch = !search || item.email.includes(search) || String(item.name || '').toLowerCase().includes(search);
    const matchesSource = filters.source === 'all' || item.sources?.includes(filters.source);
    const matchesStatus = filters.status === 'all' || item.subscribedStatus === filters.status;
    const matchesType = filters.type === 'all' || item.contactTypes?.includes(filters.type);
    return matchesSearch && matchesSource && matchesStatus && matchesType;
  }), [subscribers, filters]);
  const sendable = filtered.filter((item) => item.subscribedStatus === 'subscribed' && item.marketingOptIn);

  async function setSubscriptionStatus(email, subscribedStatus) {
    await api('/api/admin/newsletter/subscribers/status', {
      method: 'PATCH',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify({ email, subscribedStatus })
    });
    setMessage(`${email} marked ${subscribedStatus}.`);
    await refresh();
  }

  async function sendTest(event) {
    event.preventDefault();
    const result = await api('/api/admin/email/send-test', {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify({ subject: draft.subject, message: draft.message, recipient: adminEmail })
    });
    setDraft({ ...draft, testSendId: result.testSendId });
    setMessage(`Test email ${result.record.status.toLowerCase()} to ${adminEmail}.`);
    await refresh();
  }

  async function sendCampaign() {
    const recipients = draft.recipientMode === 'specific' ? sendable.map((item) => item.email) : [];
    const count = draft.recipientMode === 'specific' ? recipients.length : subscribers.filter((item) => item.subscribedStatus === 'subscribed' && item.marketingOptIn).length;
    if (!draft.testSendId) {
      setMessage('Send a test email before launching a campaign.');
      return;
    }
    if (!window.confirm(`Send this campaign to ${count} subscribed recipient${count === 1 ? '' : 's'} from ${supportEmail}?`)) return;
    const result = await api('/api/admin/email/send-campaign', {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify({
        subject: draft.subject,
        message: draft.message,
        testSendId: draft.testSendId,
        recipientMode: draft.recipientMode,
        selectedEmails: recipients,
        confirmSend: true
      })
    });
    setMessage(`Campaign ${result.campaign.status.toLowerCase()} for ${result.campaign.recipientCount} subscribed recipients.`);
    await refresh();
  }

  return (
    <section className="admin-section email-center" id="audience-and-email">
      <div className="admin-section-heading">
        <div>
          <h2>Audience & Email</h2>
          <p>Consent-based subscriber management, account-contact visibility, exports, and campaign sending from {supportEmail}.</p>
        </div>
        <a className="button secondary" href="/api/admin/newsletter/export">Export CSV</a>
      </div>

      <div className="audience-summary-grid">
        {[
          ['Total contacts', audience?.summary?.total || 0],
          ['Subscribed', audience?.summary?.subscribed || 0],
          ['Unsubscribed', audience?.summary?.unsubscribed || 0],
          ['Account contacts', audience?.summary?.accountContacts || 0]
        ].map(([label, value]) => (
          <article className="admin-card" key={label}>
            <Mail size={18} />
            <span>{label}</span>
            <strong>{number(value)}</strong>
          </article>
        ))}
      </div>

      <div className="email-admin-grid">
        <article className="ops-panel audience-list-panel">
          <div className="panel-head">
            <h3>Master contact list</h3>
            <span>{number(filtered.length)} visible</span>
          </div>
          <div className="audience-filters">
            <label>
              <span>Search</span>
              <input value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} placeholder="Search email or name" />
            </label>
            <label>
              <span>Source</span>
              <select value={filters.source} onChange={(event) => setFilters({ ...filters, source: event.target.value })}>
                {sources.map((source) => <option value={source} key={source}>{source}</option>)}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
                <option value="all">All</option>
                <option value="subscribed">Subscribed</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="not_subscribed">Account contacts only</option>
              </select>
            </label>
            <label>
              <span>Type</span>
              <select value={filters.type} onChange={(event) => setFilters({ ...filters, type: event.target.value })}>
                <option value="all">All</option>
                <option value="developer_updates">Developer updates</option>
                <option value="newsletter">Newsletter</option>
                <option value="account">Account contacts</option>
              </select>
            </label>
          </div>
          <div className="audience-list">
            {filtered.slice(0, 80).map((item) => (
              <div className="audience-row" key={item.email}>
                <div>
                  <strong>{item.email}</strong>
                  <span>{item.name || 'No name'} · {item.sources?.join(', ') || 'Unknown source'}</span>
                  <small>{item.subscribedStatus.replace('_', ' ')} · {item.consentTimestamp || 'No marketing consent timestamp'}</small>
                </div>
                <div className="audience-actions">
                  {item.subscribedStatus === 'subscribed' ? (
                    <button type="button" onClick={() => setSubscriptionStatus(item.email, 'unsubscribed')}>Unsubscribe</button>
                  ) : (
                    <button type="button" onClick={() => setSubscriptionStatus(item.email, 'subscribed')}>Resubscribe</button>
                  )}
                </div>
              </div>
            ))}
            {!filtered.length && <p className="empty-state">No contacts match those filters.</p>}
          </div>
        </article>

        <form className="ops-panel email-composer" onSubmit={sendTest}>
          <div className="panel-head">
            <h3>Compliant campaign composer</h3>
            <span>From {supportEmail}</span>
          </div>
          <label>
            <span>Recipient selector</span>
            <select value={draft.recipientMode} onChange={(event) => setDraft({ ...draft, recipientMode: event.target.value, testSendId: '' })}>
              <option value="all_subscribed">All subscribed users</option>
              <option value="specific">Current filtered subscribed group</option>
            </select>
          </label>
          <label>
            <span>Subject</span>
            <input value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value, testSendId: '' })} />
          </label>
          <label>
            <span>Email body</span>
            <textarea value={draft.message} onChange={(event) => setDraft({ ...draft, message: event.target.value, testSendId: '' })} rows={10} />
          </label>
          <div className="compliance-note">
            <ShieldCheck size={17} />
            <span>Campaigns are limited to opted-in subscribers and must include a clear unsubscribe line.</span>
          </div>
          <button className="button secondary" type="submit">Send Test To Admin</button>
          <button className="button primary" type="button" onClick={sendCampaign}>
            Send Campaign To {draft.recipientMode === 'specific' ? number(sendable.length) : number(audience?.summary?.subscribed || 0)} Subscribers
          </button>
        </form>
      </div>

      {message && <p className="form-message success">{message}</p>}
      <DataTable
        title="Recent email sends"
        rows={audience?.outboundEmails || []}
        columns={['kind', 'from', 'recipient', 'subject', 'recipientCount', 'status', 'createdAt']}
        empty="No email sends yet."
      />
    </section>
  );
}

function AdminContentStudio({ posts, refresh }) {
  const emptyDraft = {
    type: 'news',
    status: 'draft',
    headline: '',
    caption: '',
    browserTitle: '',
    metaDescription: '',
    heroLabel: '',
    category: '',
    tags: '',
    body: ''
  };
  const [draft, setDraft] = useState(emptyDraft);
  const [message, setMessage] = useState('');
  const published = posts.filter((post) => post.status === 'published');

  function editPost(post) {
    setDraft({
      id: post.id,
      type: post.type || 'news',
      status: post.status || 'draft',
      headline: post.headline || '',
      caption: post.caption || '',
      browserTitle: post.browserTitle || '',
      metaDescription: post.metaDescription || '',
      heroLabel: post.heroLabel || '',
      category: post.category || '',
      tags: (post.tags || []).join(', '),
      body: post.body || ''
    });
    setMessage(`Editing ${post.headline}.`);
  }

  async function savePost(event) {
    event.preventDefault();
    const result = await api('/api/admin/ai-content/posts', {
      method: 'POST',
      headers: { 'x-csrf-token': getCookie('wc_csrf_token') || '' },
      body: JSON.stringify(draft)
    });
    setMessage(`${result.post.status === 'published' ? 'Published' : 'Saved draft'}: ${result.post.headline}`);
    setDraft(emptyDraft);
    await refresh();
  }

  return (
    <section className="admin-section content-studio" id="ai-content">
      <div className="admin-section-heading">
        <div>
          <h2>AI Content Studio</h2>
          <p>Publish News &amp; Media articles and Updates posts with shareable article pages, browser titles, captions, metadata, tags, and body copy.</p>
        </div>
        <a className="button secondary" href="https://ai.whitestonecomputing.com/news" target="_blank" rel="noreferrer">View AI Site</a>
      </div>

      <div className="email-admin-grid">
        <form className="ops-panel email-composer" onSubmit={savePost}>
          <div className="panel-head">
            <h3>{draft.id ? 'Edit content' : 'Create content'}</h3>
            <span>{draft.type === 'news' ? 'News & Media' : 'Updates'}</span>
          </div>
          <label>
            <span>Content type</span>
            <select value={draft.type} onChange={(event) => setDraft({ ...draft, type: event.target.value })}>
              <option value="news">News &amp; Media article</option>
              <option value="update">Update blog post</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </label>
          <label>
            <span>Headline</span>
            <input value={draft.headline} onChange={(event) => setDraft({ ...draft, headline: event.target.value })} placeholder="Article headline" required />
          </label>
          <label>
            <span>Caption</span>
            <input value={draft.caption} onChange={(event) => setDraft({ ...draft, caption: event.target.value })} placeholder="Short social caption / preview text" required />
          </label>
          <label>
            <span>Browser title</span>
            <input value={draft.browserTitle} onChange={(event) => setDraft({ ...draft, browserTitle: event.target.value })} placeholder="Defaults to headline" />
          </label>
          <label>
            <span>Meta description</span>
            <input value={draft.metaDescription} onChange={(event) => setDraft({ ...draft, metaDescription: event.target.value })} placeholder="Search/social description" />
          </label>
          <label>
            <span>Hero label</span>
            <input value={draft.heroLabel} onChange={(event) => setDraft({ ...draft, heroLabel: event.target.value })} placeholder="News & Media, Platform update, Course drop..." />
          </label>
          <label>
            <span>Category</span>
            <input value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })} placeholder="AI tools, Education, Company update..." />
          </label>
          <label>
            <span>Tags</span>
            <input value={draft.tags} onChange={(event) => setDraft({ ...draft, tags: event.target.value })} placeholder="ai, courses, tools" />
          </label>
          <label>
            <span>Article body</span>
            <textarea value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} rows={12} required />
          </label>
          <button className="button primary" type="submit">{draft.status === 'published' ? 'Publish Content' : 'Save Draft'}</button>
          {draft.id && <button className="button secondary" type="button" onClick={() => setDraft(emptyDraft)}>New Post</button>}
        </form>

        <article className="ops-panel audience-list-panel">
          <div className="panel-head">
            <h3>AI content library</h3>
            <span>{number(posts.length)} posts / {number(published.length)} published</span>
          </div>
          <div className="audience-list">
            {posts.slice(0, 80).map((post) => (
              <div className="audience-row" key={post.id}>
                <div>
                  <strong>{post.headline}</strong>
                  <span>{post.type === 'news' ? 'News & Media' : 'Update'} · {post.status} · /{post.sharePath}</span>
                  <small>{post.metaDescription || post.caption}</small>
                </div>
                <div className="audience-actions">
                  <button type="button" onClick={() => editPost(post)}>Edit</button>
                  <a href={`https://ai.whitestonecomputing.com/article.html?slug=${post.slug}`} target="_blank" rel="noreferrer">Open</a>
                </div>
              </div>
            ))}
            {!posts.length && <p className="empty-state">No AI content posts yet.</p>}
          </div>
        </article>
      </div>

      {message && <p className="form-message success">{message}</p>}
    </section>
  );
}

function AdminModuleRegistry({ registry }) {
  const [moduleFilter, setModuleFilter] = useState('all');
  const modules = registry?.modules || [];
  const visibleModules = moduleFilter === 'all' ? modules : modules.filter((item) => item.category === moduleFilter || item.productId === moduleFilter);
  const productRows = registry?.byProduct || [];

  return (
    <section className="admin-section module-registry" id="modules">
      <div className="admin-section-heading">
        <div>
          <h2>Portable Software Module Registry</h2>
          <p>Break every Whitestone SaaS into reusable functions you can package, move, and drop into future apps.</p>
        </div>
        <div className="module-summary">
          <strong>{number(registry?.summary.totalModules || 0)}</strong>
          <span>downloadable modules</span>
        </div>
      </div>

      <div className="module-map">
        {productRows.map((product) => (
          <article className="module-product-lane" key={product.productId}>
            <div className="panel-head">
              <h3>{product.productName}</h3>
              <span>{number(product.modules.length)} modules</span>
            </div>
            <div className="lane-dots">
              {product.modules.map((item) => <a href={`#module-${item.id}`} key={item.id}>{item.name}</a>)}
            </div>
          </article>
        ))}
      </div>

      <div className="module-toolbar">
        <label>
          <span>Filter module library</span>
          <select value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)}>
            <option value="all">All modules</option>
            {(registry?.categories || []).map((category) => <option value={category} key={category}>{category}</option>)}
            <option value="shared">Whitestone shared core</option>
          </select>
        </label>
        <p>Use these packages as starter bundles. They intentionally keep secrets out and expose the route/component shape you can transplant.</p>
      </div>

      <div className="module-grid">
        {visibleModules.map((module) => (
          <article className="module-card" id={`module-${module.id}`} key={module.id}>
            <div className="product-topline">
              <span className="product-icon"><Layers3 size={21} /></span>
              <span className={`status ${module.status.toLowerCase().includes('internal') ? 'beta' : ''}`}>{module.status}</span>
            </div>
            <h3>{module.name}</h3>
            <p>{module.description}</p>
            <div className="module-meta">
              <span>{module.productName}</span>
              <span>{module.category}</span>
              <span>{module.portability}</span>
            </div>
            <div className="function-chips">
              {module.functions.map((item) => <span key={item}>{item}</span>)}
            </div>
            <details>
              <summary>Package contents and env</summary>
              <div className="module-details">
                <strong>Files</strong>
                <p>{module.packageFiles.join(', ')}</p>
                <strong>Env</strong>
                <p>{module.env.join(', ')}</p>
              </div>
            </details>
            <a className="button secondary" href={`/api/admin/software-modules/${module.id}/package.zip`}>
              <FileText size={17} /> Download Package
            </a>
          </article>
        ))}
      </div>
    </section>
  );
}

function AdminFrame({ children, logout }) {
  const [adminNavOpen, setAdminNavOpen] = useState(false);
  const adminLinks = ['Overview', 'Products', 'Analytics', 'Revenue', 'Monitoring', 'PayStub Instant', 'Modules', 'Audience & Email', 'AI Content', 'Users', 'Access Requests', 'Support', 'Audit Logs'];

  useEffect(() => {
    document.body.classList.toggle('admin-nav-open', adminNavOpen);
    return () => document.body.classList.remove('admin-nav-open');
  }, [adminNavOpen]);

  return (
    <main className="admin-shell">
      <header className="admin-mobile-header">
        <Link className="brand" to="/">
          <BrandMark />
          <span><strong>Whitestone</strong><small>Central</small></span>
        </Link>
        <button className="icon-button" type="button" aria-label="Open admin navigation" aria-expanded={adminNavOpen} onClick={() => setAdminNavOpen(!adminNavOpen)}>
          {adminNavOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>
      {adminNavOpen && <button className="admin-nav-backdrop" type="button" aria-label="Close admin navigation" onClick={() => setAdminNavOpen(false)} />}
      <aside className={adminNavOpen ? 'admin-sidebar open' : 'admin-sidebar'}>
        <Link className="brand admin-sidebar-brand" to="/" onClick={() => setAdminNavOpen(false)}>
          <BrandMark />
          <span><strong>Whitestone</strong><small>Central</small></span>
        </Link>
        <nav>
          {adminLinks.map((item) => (
            <a href={`#${item.toLowerCase().replaceAll(' & ', '-and-').replaceAll(' ', '-')}`} key={item} onClick={() => setAdminNavOpen(false)}>{item}</a>
          ))}
        </nav>
        <button className="button ghost" type="button" onClick={logout}>
          <LogOut size={17} /> Logout
        </button>
      </aside>
      <div className="admin-main">{children}</div>
    </main>
  );
}

function ProductAdminCard({ product }) {
  const metrics = [
    ['Users', product.totalUsers],
    ['Active', product.activeUsers],
    ['Free', product.freeUsers],
    ['Paid', product.paidUsers],
    ['Revenue', currency(product.revenue)],
    ['MRR', currency(product.monthlyRevenue)],
    ['Subscriptions', product.subscriptionCount],
    ['Trials', product.trialUsers],
    ['Cancellations', product.cancellations],
    ['Failed payments', product.failedPayments],
    ['Access requests', product.inviteRequests],
    ['Tickets', product.supportTickets],
    ['Conversion', `${product.conversionRate}%`],
    ['Usage', product.usageCount],
    ['7 days', product.last7DaysActivity],
    ['30 days', product.last30DaysActivity],
    ['Connector', product.connectorRegistered ? 'Registered' : 'Missing'],
    ['API env', product.backendConfigured ? 'Configured' : 'Needs env'],
    ['Events', product.analyticsEvents?.length || 0],
    ['Collections', product.dataCollections?.length || 0]
  ];

  return (
    <article className="product-admin-card">
      <div className="product-admin-head">
        <div>
          <h3>{product.name}</h3>
          <span>{product.category}</span>
        </div>
        <strong>{product.productStatus}</strong>
      </div>
      <div className="metric-grid">
        {metrics.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{typeof value === 'number' ? number(value) : value}</strong>
          </div>
        ))}
      </div>
      {product.backendCapabilities?.length > 0 && (
        <div className="function-chips">
          {product.backendCapabilities.map((capability) => <span key={capability}>{capability}</span>)}
        </div>
      )}
      <div className="quick-actions">
        {product.quickActions.map((action) => <button type="button" key={action}>{action}</button>)}
      </div>
    </article>
  );
}

function ChartPanel({ title, data, money, suffix = '' }) {
  const max = Math.max(...data.map((item) => item.value), 1);
  return (
    <article className="chart-panel">
      <h3>{title}</h3>
      <div className="bar-list">
        {data.map((item) => (
          <div className="bar-row" key={item.label}>
            <span>{item.label}</span>
            <div className="bar-track"><i style={{ width: `${Math.max((item.value / max) * 100, 3)}%` }} /></div>
            <strong>{money ? currency(item.value) : `${number(item.value)}${suffix}`}</strong>
          </div>
        ))}
      </div>
    </article>
  );
}

function DataTable({ title, rows, columns, moneyColumn, empty = 'No records.' }) {
  return (
    <article className="data-table">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="empty-state">{empty}</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>{columns.map((column) => <th key={column}>{labelize(column)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {columns.map((column) => (
                    <td key={column}>{moneyColumn === column ? currency(row[column]) : formatCell(row[column], column)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function Footer({ isAdmin }) {
  const footerGroups = [
    {
      title: 'Company',
      links: [
        ['/', 'Home'],
        ['/about', 'About Whitestone'],
        ['/about#mission', 'Our Mission']
      ]
    },
    {
      title: 'Platforms',
      links: [
        ['/ai', 'AI News and Education'],
        ['/apis', 'Developer APIs'],
        ['/products', 'Product Brands'],
        ...(isAdmin ? [['/admin', 'Central Admin']] : [])
      ]
    },
    {
      title: 'Connect',
      links: [
        ['/work-with-us', 'Work With Us'],
        ['/contact', 'Contact'],
        ['/support', 'Support']
      ]
    },
    {
      title: 'Legal',
      links: [
        ['/privacy', 'Privacy Policy'],
        ['/terms', 'Terms of Service'],
        ['/copyright', 'Trademarks / Copyright']
      ]
    }
  ];

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-brand-block">
          <Link className="brand" to="/">
            <BrandMark />
            <span><strong>Whitestone Computing</strong><small>whitestonecomputing.com</small></span>
          </Link>
          <p>
            Whitestone Computing LLC builds practical software, AI education, developer tools, automation products, document utilities,
            productivity systems, and AI education for modern teams and independent builders.
          </p>
          <p>© 2026 Whitestone Computing LLC. All rights reserved.</p>
          <p>
            Whitestone Computing LLC, Whitestone Computing, and associated product names, logos, software names, and brand marks are
            trademarks or service marks of Whitestone Computing LLC unless otherwise stated.
          </p>
        </div>
        <nav className="footer-link-groups" aria-label="Footer navigation">
          {footerGroups.map((group) => (
            <div className="footer-link-group" key={group.title}>
              <span>{group.title}</span>
              {group.links.map(([to, label]) => <Link key={to} to={to}>{label}</Link>)}
            </div>
          ))}
        </nav>
        <div className="footer-contact">
          <span>Support Desk</span>
          <a href={`mailto:${supportEmail}`}>{supportEmailDisplay}</a>
          <small>For product questions, support, partnerships, access requests, and general Whitestone Computing inquiries.</small>
        </div>
      </div>
    </footer>
  );
}

function NotFound() {
  return (
    <PageShell eyebrow="404" title="Page not found">
      <Link className="button primary" to="/">Return Home</Link>
    </PageShell>
  );
}

function ScrollToTop() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [hash, pathname]);
  return null;
}

function currency(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);
}

function number(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value || 0);
}

function labelize(value) {
  return String(value || '').replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').replace(/^./, (letter) => letter.toUpperCase());
}

function productName(value) {
  const normalized = String(value || '').toLowerCase();
  return products.find((product) => product.id === normalized)?.name || value;
}

function formatCell(value, column = '') {
  if (!value) return '-';
  if (column.toLowerCase().includes('product')) return productName(value);
  if (typeof value === 'string' && value.includes('T')) return new Date(value).toLocaleDateString();
  return value;
}

export default App;
